/**
 * 实体选择事件管理器。
 * 职责：
 * 1. 监听原始鼠标事件；
 * 2. 将底层指针事件转换为实体语义事件；
 * 3. 维护当前 hover / 选中 / 框选状态；
 * 4. 为上层选择查询与拖拽策略提供统一运行时接口。
 */

import * as THREE from 'three/webgpu';
import {
    SketchArcObject,
    SketchCircleObject,
    SketchEllipseObject,
    SketchLineObject,
    SketchPointObject,
    isSketchArcObject,
    isSketchCircleObject,
    isSketchEllipseObject,
    isSketchSlotObject,
    isSketchSplineObject,
    isSketchStyleSplineObject,
} from '../mesh/sketch';
import {
    isFeatureFace,
    isSketchContour,
    type SketchContour,
} from '../mesh';
import {
    isFeatureLine,
    isFeaturePoint,
    type FeatureFace,
    type FeatureLine,
    type FeaturePoint,
} from '../mesh/feature';
import {FeatureResultMesh, isFeatureResultMesh} from '../mesh/feature/FeatureResultMesh';
import { type Observable, Subject, Subscription } from 'rxjs';
import { EventManager, type RawPointerEventPayload, RawPointerEventType } from '../viewer/moudles/EventManager';
import type { Viewer } from '../viewer/Viewer';
import { ViewerMode } from '../viewer/ViewerMode';
import {
    resolveBRepGeometryDescriptor,
    resolveAssemblyReferenceSelectionSnapshotEntry,
    resolveOwningFeatureResultMesh,
    resolveTopologySelectionPick,
} from '../utils/feature';

import {type AbstractEntityDragStrategy, ContourDragStrategy, PointDragStrategy} from './drag';
import {
    type AbstractEntitySelectionHandler,
    type EntityDragCapabilityConfig,
} from './entitySelection/AbstractEntitySelectionHandler';
import { AssemblyEntitySelectionHandler } from './entitySelection/AssemblyEntitySelectionHandler';
import { EntityHoverInputScheduler } from './entitySelection/input';
import { DefaultEntitySelectionHandler } from './entitySelection/DefaultEntitySelectionHandler';
import { LinearComponentPatternEntitySelectionHandler } from './entitySelection/LinearComponentPatternEntitySelectionHandler';
import { MoveComponentEntitySelectionHandler } from './entitySelection/MoveComponentEntitySelectionHandler';
import { ReferenceAxisEntitySelectionHandler } from './entitySelection/ReferenceAxisEntitySelectionHandler';
import { ReferencePlaneEntitySelectionHandler } from './entitySelection/ReferencePlaneEntitySelectionHandler';
import { ReferencePointEntitySelectionHandler } from './entitySelection/ReferencePointEntitySelectionHandler';
import { AssemblyReferenceAxisEntitySelectionHandler } from './entitySelection/AssemblyReferenceAxisEntitySelectionHandler';
import { AssemblyReferencePlaneEntitySelectionHandler } from './entitySelection/AssemblyReferencePlaneEntitySelectionHandler';
import { AssemblyReferencePointEntitySelectionHandler } from './entitySelection/AssemblyReferencePointEntitySelectionHandler';
import { MeasureEntitySelectionHandler } from './entitySelection/MeasureEntitySelectionHandler';
import { MeasurePickScopeResolver } from '../measure/selection/MeasurePickScopeResolver';
import { SketchEntitySelectionHandler } from './entitySelection/SketchEntitySelectionHandler';
import { SketchMoveEntitiesSelectionHandler } from './entitySelection/SketchMoveEntitiesSelectionHandler';
import { SketchCopyEntitiesSelectionHandler } from './entitySelection/SketchCopyEntitiesSelectionHandler';
import { SketchRotateEntitiesSelectionHandler } from './entitySelection/SketchRotateEntitiesSelectionHandler';
import { SketchCircularPatternSelectionHandler } from './entitySelection/SketchCircularPatternSelectionHandler';
import { SketchMirrorEntitiesSelectionHandler } from './entitySelection/SketchMirrorEntitiesSelectionHandler';
import { SketchTrimEntitySelectionHandler } from './entitySelection/SketchTrimEntitySelectionHandler';
import { SketchDimensionEntitySelectionHandler } from './entitySelection/SketchDimensionEntitySelectionHandler';
import { SketchOffsetEntitySelectionHandler } from './entitySelection/SketchOffsetEntitySelectionHandler';
import type { SelectionInteractionMeta } from '../imported-model/render/selection-extension';
import { ImportedModelObjectMetadataService } from '../imported-model/metadata';
import { ImportedModelRaycastResolver } from '../imported-model/selection/ImportedModelRaycastResolver';
import { ImportedModelTopologyProxyRetentionController } from '../imported-model/selection/ImportedModelTopologyProxyRetentionController';
import { resolveImportedAssemblyTopologySelectionRef } from '../imported-model/selection/resolveImportedAssemblyTopologySelectionRef';
import { resolveModelBodySceneRegistry } from '../model-body/model-body-scene-registry-runtime';
import {ScreenSpacePrecisionPicker} from './pick';
import type {EntitySelectionManagerRuntime} from './selection/EntitySelectionRuntime';
import {
    normalizeEntitySelectionMode,
    type EntitySelectionMode,
    type EntitySelectionModeOptions,
} from './selection/EntitySelectionMode';
import {isSketchObjectSelectableInActiveSketch} from './SketchEditingInteractionGuard';
import * as SelectionQueryHelpers from './selection/EntitySelectionQueryHelpers';
import * as SelectionStateHelpers from './selection/EntitySelectionStateHelpers';
import {
    EntityInteractionEventType,
    EntitySelectionCandidateIndex,
    EntitySelectionQueryPartitioner,
} from './selection';

// 保留既有模块级公开出口；真实定义已下沉到无管理器依赖的选择协议层。
export {EntityInteractionEventType} from './selection';

/**
 * 实体移入事件载荷。
 */
export interface EntityHoverEnterEventPayload {
    /** 事件类型。 */
    type: typeof EntityInteractionEventType.EntityHoverEnter;
    /** 当前移入实体 ID。 */
    objectId: string;
    /** 当前移入命中的 Three.js 对象。 */
    object: THREE.Object3D;
    interactionMeta?: SelectionInteractionMeta;
    /** 事件时间戳。 */
    timestamp: number;
}

/**
 * 实体移出事件载荷。
 */
export interface EntityHoverLeaveEventPayload {
    /** 事件类型。 */
    type: typeof EntityInteractionEventType.EntityHoverLeave;
    /** 当前移出实体 ID。 */
    objectId: string;
    /** 当前移出命中的 Three.js 对象。 */
    object: THREE.Object3D;
    interactionMeta?: SelectionInteractionMeta;
    /** 事件时间戳。 */
    timestamp: number;
}

/**
 * 实体选中事件载荷。
 */
export interface EntitySelectedEventPayload {
    /** 事件类型。 */
    type: typeof EntityInteractionEventType.EntitySelected;
    /** 当前主选实体 ID。 */
    objectId: string | null;
    /** 当前主选命中的 Three.js 对象。 */
    object: THREE.Object3D | null;
    /** 当前对象级选中实体 ID 集合。 */
    selectedObjectIds: string[];
    /** 当前对象级选中 Three.js 对象集合。 */
    selectedObjects: THREE.Object3D[];
    interactionMeta?: SelectionInteractionMeta;
    /** 事件时间戳。 */
    timestamp: number;
}

//------ 特征实体选择事件 开始------
/**
 * 特征实体选中事件公共载荷。
 *
 * @template TObject 特征对象类型。
 * @template TEventType 事件枚举类型。
 */
interface FeatureEntitySelectedEventPayload<
    TObject extends THREE.Object3D,
    TEventType extends PuppyCAD.EntityInteractionEventType,
> {
    /** 事件类型。 */
    type: TEventType;
    /** 当前主选实体 ID。 */
    objectId: string;
    /** 当前主选特征对象。 */
    object: TObject;
    /** 当前对象级选中实体 ID 集合。 */
    selectedObjectIds: string[];
    /** 当前对象级选中特征对象集合。 */
    selectedObjects: TObject[];
    /** 事件时间戳。 */
    timestamp: number;
}

/**
 * 特征面选中事件载荷。
 */
export interface FeatureFaceSelectedEventPayload extends FeatureEntitySelectedEventPayload<
    FeatureFace,
    typeof EntityInteractionEventType.FeatureFaceSelected
> {}

/**
 * 特征线选中事件载荷。
 */
export interface FeatureLineSelectedEventPayload extends FeatureEntitySelectedEventPayload<
    FeatureLine,
    typeof EntityInteractionEventType.FeatureLineSelected
> {}

/**
 * 特征点选中事件载荷。
 */
export interface FeaturePointSelectedEventPayload extends FeatureEntitySelectedEventPayload<
    FeaturePoint,
    typeof EntityInteractionEventType.FeaturePointSelected
> {}
//------ 特征实体选择事件 结束------

/**
 * 实体框选事件载荷。
 * 閫夋嫨鐘舵€佸悓姝ュ畬鎴愪簨浠惰浇鑽枫€?
 */
/**
 * 瀹炰綋妗嗛€変簨浠惰浇鑽枫€?
 */
export interface EntityBoxSelectedEventPayload {
    /** 事件类型。 */
    type: typeof EntityInteractionEventType.EntityBoxSelected;
    /** 本次框选命中的实体 ID 列表。 */
    selectedObjectIds: string[];
    /** 本次框选命中的 Three.js 对象列表。 */
    selectedObjects: THREE.Object3D[];
    /** 本次框选是否为 crossing 模式。 */
    isCrossing: boolean;
    /** 本次框选的屏幕矩形。 */
    selectionRect: {
        /** 屏幕空间最小 X 坐标。 */
        minX: number;
        /** 屏幕空间最小 Y 坐标。 */
        minY: number;
        /** 屏幕空间最大 X 坐标。 */
        maxX: number;
        /** 屏幕空间最大 Y 坐标。 */
        maxY: number;
    };
    /** 事件时间戳。 */
    timestamp: number;
}

/**
 * 点选中事件载荷。
 */
export interface PointSelectedEventPayload {
    /** 事件类型。 */
    type: typeof EntityInteractionEventType.PointSelected;
    /** 当前选中点对象 ID。 */
    objectId: string;
    /** 当前选中的点对象。 */
    object: SketchPointObject;
    /** 事件时间戳。 */
    timestamp: number;
}

/**
 * 线选中事件载荷。
 */
export interface LineSelectedEventPayload {
    /** 事件类型。 */
    type: typeof EntityInteractionEventType.LineSelected;
    /** 当前选中线对象 ID。 */
    objectId: string;
    /** 当前选中的线对象。 */
    object: SketchLineObject;
    /** 事件时间戳。 */
    timestamp: number;
}

/**
 * 圆选中事件载荷。
 */
export interface CircleSelectedEventPayload {
    /** 事件类型。 */
    type: typeof EntityInteractionEventType.CircleSelected;
    /** 当前选中圆对象 ID。 */
    objectId: string;
    /** 当前选中的圆对象。 */
    object: SketchCircleObject;
    /** 事件时间戳。 */
    timestamp: number;
}

/**
 * 圆弧选中事件载荷。
 */
export interface ArcSelectedEventPayload {
    /** 事件类型。 */
    type: typeof EntityInteractionEventType.ArcSelected;
    /** 当前选中圆弧对象 ID。 */
    objectId: string;
    /** 当前选中的圆弧对象。 */
    object: SketchArcObject;
    /** 事件时间戳。 */
    timestamp: number;
}

/**
 * 椭圆选中事件载荷。
 */
export interface EllipseSelectedEventPayload {
    /** 事件类型。 */
    type: typeof EntityInteractionEventType.EllipseSelected;
    /** 当前选中椭圆对象 ID。 */
    objectId: string;
    /** 当前选中的椭圆对象。 */
    object: SketchEllipseObject;
    /** 事件时间戳。 */
    timestamp: number;
}

/**
 * 样条曲线选中事件载荷。
 */
export interface SplineSelectedEventPayload {
    /** 事件类型。 */
    type: typeof EntityInteractionEventType.SplineSelected;
    /** 当前选中样条曲线对象 ID。 */
    objectId: string;
    /** 当前选中的样条曲线对象。 */
    object: THREE.Object3D;
    /** 事件时间戳。 */
    timestamp: number;
}

/**
 * 样式样条曲线选中事件载荷。
 */
export interface StyleSplineSelectedEventPayload {
    /** 事件类型。 */
    type: typeof EntityInteractionEventType.StyleSplineSelected;
    /** 当前选中样式样条曲线对象 ID。 */
    objectId: string;
    /** 当前选中的样式样条曲线对象。 */
    object: THREE.Object3D;
    /** 事件时间戳。 */
    timestamp: number;
}

/**
 * 槽口选中事件载荷。
 */
export interface SlotSelectedEventPayload {
    /** 事件类型。 */
    type: typeof EntityInteractionEventType.SlotSelected;
    /** 当前选中槽口对象 ID。 */
    objectId: string;
    /** 当前选中的槽口对象。 */
    object: THREE.Object3D;
    /** 事件时间戳。 */
    timestamp: number;
}

/**
 * 选择清空事件载荷。
 */
export interface SelectionClearedEventPayload {
    /** 事件类型。 */
    type: typeof EntityInteractionEventType.SelectionCleared;
    /** 事件时间戳。 */
    timestamp: number;
}

/**
 * 装配体拓扑选择成对快照。
 *
 * 设计说明：
 * 单次 pick 的拓扑引用与 BRep 几何描述符必须成对保留。若分别过滤成两个数组后再按 index
 * 拼接，混入缺少拓扑或缺少几何的对象时会错配，Mate 持久引用会指向错误实体。
 */
export interface AssemblyTopologySelectionSnapshotEntry {
    /** 本次 pick 的对象 ID。 */
    objectId: string;
    /** 权威拓扑命中。 */
    topologyPick: ResolvedTopologyPick;
    /** 装配体拓扑稳定引用。 */
    assemblyTopologyRef: AssemblyTopologySelectionRef;
    /** BRep 几何描述符。 */
    brepGeometryDescriptor: BRepGeometryDescriptor;
}

/**
 * 装配体基准引用选择成对快照。
 *
 * 设计说明：
 * 1. 基准点/轴/面不属于 imported BRep 子拓扑，不能被包装成 `AssemblyTopologySelectionRef`；
 * 2. 该快照只保存稳定基准引用和世界空间几何描述符，供 Mate 等命令直接消费；
 * 3. web-client 不需要也不允许从 Object3D 反推基准引用。
 */
export type AssemblyReferenceSelectionSnapshotEntry = PuppyCAD.AssemblyReferenceSelectionSnapshotEntry;

/**
 * 选择更新的交互意图。
 *
 * 设计说明：
 * 1. Mate、参考几何体等命令都消费 SelectionUpdated，必须能区分“点击选择”和“拖拽启动前的选择高亮”；
 * 2. 未携带该字段的历史事件仍按普通选择处理，避免破坏旧调用方和测试夹具。
 */
export type SelectionUpdateIntent =
    /** 指针按下产生的真实视口选择。 */
    | 'pointer-down'
    /** 拖拽启动前产生的临时选择同步。 */
    | 'drag-start'
    /** 设计树特征局部高亮前清空真实实体选择。 */
    | 'feature-tree-visual-scope';

/**
 * 选择状态更新选项。
 */
export interface SelectionUpdateOptions {
    /** 本次选择更新的交互意图。 */
    readonly intent?: SelectionUpdateIntent;
}

/**
 * 选择结算事件载荷。
 *
 * 设计说明：
 * 1. 该事件在一次选择处理完成后始终派发，不要求选中集合真的发生变化；
 * 2. 圆角/倒角这类宿主命令依赖“当前主选对象快照”，不能继续只监听 `EntitySelected`；
 * 3. 统一在选择层提供稳定快照，避免上层再自行拼接 `SelectionCleared + EntitySelected`。
 */
export interface SelectionUpdatedEventPayload {
    /** 事件类型。 */
    type: typeof EntityInteractionEventType.SelectionUpdated;
    /** 当前主选实体 ID。 */
    objectId: string | null;
    /** 当前主选实体对象。 */
    object: THREE.Object3D | null;
    /**
     * 当前主选对应的原始命中实体 ID。
     * 说明：
     * 1. object/objectId 表达“选择语义”；
     * 2. selectionSourceObject 表达“本次鼠标真正点到的对象”；
     * 3. 例如槽口边点击会被普通选择提升为槽口整体，但转换实体引用仍需要知道原始边。
     */
    selectionSourceObjectId: string | null;
    /** 当前主选对应的原始命中实体对象。 */
    selectionSourceObject: THREE.Object3D | null;
    /** 当前已选实体 ID 列表。 */
    selectedObjectIds: string[];
    /** 当前已选实体对象列表。 */
    selectedObjects: THREE.Object3D[];
    /** 本次选择交互的原始命中实体 ID 列表。 */
    selectionSourceObjectIds: string[];
    /** 本次选择交互的原始命中实体对象列表。 */
    selectionSourceObjects: THREE.Object3D[];
    /** 当前主选对应的权威拓扑命中快照。 */
    topologyPick: ResolvedTopologyPick | null;
    /** 当前已选对象对应的权威拓扑命中快照集合。 */
    topologyPicks: ResolvedTopologyPick[];
    /** 当前主选对象对应的装配体拓扑选择引用。 */
    assemblyTopologyRef: AssemblyTopologySelectionRef | null;
    /** 当前已选对象对应的装配体拓扑选择引用集合。 */
    assemblyTopologyRefs: AssemblyTopologySelectionRef[];
    /** 当前已选对象对应的装配体拓扑引用与 BRep 描述符成对集合；旧测试载荷可省略，运行时事件始终提供。 */
    assemblyTopologySelectionEntries?: AssemblyTopologySelectionSnapshotEntry[];
    /** 当前主选对象对应的装配体基准引用。 */
    assemblyReferenceSelectionRef?: AssemblyReferenceSelectionRef | null;
    /** 当前已选对象对应的装配体基准引用集合。 */
    assemblyReferenceSelectionRefs?: AssemblyReferenceSelectionRef[];
    /** 当前已选对象对应的装配体基准引用与几何描述符成对集合；旧测试载荷可省略。 */
    assemblyReferenceSelectionEntries?: AssemblyReferenceSelectionSnapshotEntry[];
    /** 当前主选对象对应的 BRep 几何描述符。 */
    brepGeometryDescriptor: BRepGeometryDescriptor | null;
    /** 当前已选对象对应的 BRep 几何描述符集合。 */
    brepGeometryDescriptors: BRepGeometryDescriptor[];
    /**
     * 当前选择交互的可视化元信息。
     *
     * 设计说明：
     * 1. 配合、引用等命令只消费 SelectionUpdated 快照，不能回退监听 EntitySelected；
     * 2. imported-model 的精确 primitive 高亮目标已经由选择链路解析完成，这里透传可避免上层重复解析 BRep 或运行时 userData。
     */
    interactionMeta?: SelectionInteractionMeta;
    /** 本次选择更新的交互意图；未提供时表示普通点击、程序化选择或旧事件。 */
    selectionIntent?: SelectionUpdateIntent;
    /** 事件时间戳。 */
    timestamp: number;
}

/**
 * 多实体框选事件载荷。
 * 用途：
 * 1. 供几何关系面板读取；
 * 2. 区分普通框选与多实体框选语义。
 */
export interface MultipleEntitiesBoxSelectedEventPayload {
    /** 事件类型。 */
    type: typeof EntityInteractionEventType.MultipleEntitiesBoxSelected;
    /** 框选命中的实体 ID 列表。 */
    selectedObjectIds: string[];
    /** 框选命中的 Three.js 对象列表。 */
    selectedObjects: THREE.Object3D[];
    /** 框选是否为 crossing 模式。 */
    isCrossing: boolean;
    /** 事件时间戳。 */
    timestamp: number;
}

/**
 * 实体交互事件载荷映射。
 */
export interface EntityInteractionEventPayloadMap {
    /** 实体移入事件载荷。 */
    [EntityInteractionEventType.EntityHoverEnter]: EntityHoverEnterEventPayload;
    /** 实体移出事件载荷。 */
    [EntityInteractionEventType.EntityHoverLeave]: EntityHoverLeaveEventPayload;
    /** 实体选中事件载荷。 */
    [EntityInteractionEventType.EntitySelected]: EntitySelectedEventPayload;
    //------ 特征实体选择事件 开始------
    /** 特征面选中事件载荷。 */
    [EntityInteractionEventType.FeatureFaceSelected]: FeatureFaceSelectedEventPayload;
    /** 特征线选中事件载荷。 */
    [EntityInteractionEventType.FeatureLineSelected]: FeatureLineSelectedEventPayload;
    /** 特征点选中事件载荷。 */
    [EntityInteractionEventType.FeaturePointSelected]: FeaturePointSelectedEventPayload;
    //------ 特征实体选择事件 结束------
    /** 实体框选事件载荷。 */
    [EntityInteractionEventType.EntityBoxSelected]: EntityBoxSelectedEventPayload;
    /** 点选中事件载荷。 */
    [EntityInteractionEventType.PointSelected]: PointSelectedEventPayload;
    /** 线选中事件载荷。 */
    [EntityInteractionEventType.LineSelected]: LineSelectedEventPayload;
    /** 圆选中事件载荷。 */
    [EntityInteractionEventType.CircleSelected]: CircleSelectedEventPayload;
    /** 圆弧选中事件载荷。 */
    [EntityInteractionEventType.ArcSelected]: ArcSelectedEventPayload;
    /** 椭圆选中事件载荷。 */
    [EntityInteractionEventType.EllipseSelected]: EllipseSelectedEventPayload;
    /** 样条曲线选中事件载荷。 */
    [EntityInteractionEventType.SplineSelected]: SplineSelectedEventPayload;
    /** 样式样条曲线选中事件载荷。 */
    [EntityInteractionEventType.StyleSplineSelected]: StyleSplineSelectedEventPayload;
    /** 槽口选中事件载荷。 */
    [EntityInteractionEventType.SlotSelected]: SlotSelectedEventPayload;
    /** 选择清空事件载荷。 */
    [EntityInteractionEventType.SelectionCleared]: SelectionClearedEventPayload;
    /** 选择结算事件载荷。 */
    [EntityInteractionEventType.SelectionUpdated]: SelectionUpdatedEventPayload;
    /** 多实体框选事件载荷。 */
    [EntityInteractionEventType.MultipleEntitiesBoxSelected]: MultipleEntitiesBoxSelectedEventPayload;
}
//------ 管理器类型内聚声明 结束------

/**
 * 实体选择事件管理器。
 */
export class EntitySelectionManager {
    //------ 调试开关 开始------
    /**
     * 调试日志开关对象。
     * 说明：
     * 1. 可在控制台执行 `window.__selectionDebug.hover = true` 打开对应调试；
     * 2. 各开关互不影响，可单独控制。
     */
    public static readonly debug = {
        /** 是否打印鼠标移入对象日志。 */
        hover: false,
        /** 是否打印单击选择对象日志。 */
        select: false,
        /** 是否打印框选对象列表日志。 */
        boxSelect: false,
    };
    //------ 调试开关 结束------

    /** 框选判定阈值。 */
    private static readonly BOX_DRAG_THRESHOLD: number = 4;
    /** 当前选中的点对象。 */
    public currentSelectedPoint: SketchPointObject | null = null;
    /** 当前选中的线对象。 */
    public currentSelectedLine: SketchLineObject | null = null;
    /** 当前选中的圆对象。 */
    public currentSelectedCircle: SketchCircleObject | null = null;
    /** 当前选中的圆弧对象。 */
    public currentSelectedArc: SketchArcObject | null = null;
    /** 当前选中的椭圆对象。 */
    public currentSelectedEllipse: SketchEllipseObject | null = null;
    /** 当前选中的样条曲线对象。 */
    public currentSelectedSpline: THREE.Object3D | null = null;
    /** 当前选中的样式样条曲线对象。 */
    public currentSelectedStyleSpline: THREE.Object3D | null = null;
    /** 当前选中的槽口对象。 */
    public currentSelectedSlot: THREE.Object3D | null = null;
    /** 原始事件管理器。 */
    private readonly eventManager: EventManager;
    /** Viewer 实例。 */
    public readonly renderer: Viewer;
    /** 射线投射器。 */
    private readonly raycaster: THREE.Raycaster = new THREE.Raycaster();
    /** 标准化设备坐标。 */
    private readonly mouseNdc: THREE.Vector2 = new THREE.Vector2();
    //------ 基准参考元素屏幕空间像素拾取 开始------
    /** 屏幕空间精确拾取器。 */
    public readonly screenSpacePrecisionPicker: ScreenSpacePrecisionPicker = new ScreenSpacePrecisionPicker();
    /** 拖动点捕捉复用的活动草图边候选数组，避免 pointermove 热路径重复分配。 */
    private readonly pointSnapEdgeCandidates: THREE.Line[] = [];
    //------ 基准参考元素屏幕空间像素拾取 结束------
    /** 导入拓扑临时代理保留控制器。 */
    private readonly importedTopologyProxyRetentionController: ImportedModelTopologyProxyRetentionController
        = new ImportedModelTopologyProxyRetentionController();
    /** 当前是否已启动。 */
    private isStarted: boolean = false;
    /** 当前激活模式（由 Viewer.setMode 显式推送，不再懒读取 renderer.getMode）。 */
    private currentMode: ViewerMode = ViewerMode.Default;
    private currentHoverInteractionMeta: SelectionInteractionMeta | undefined;
    private currentSelectionInteractionMeta: SelectionInteractionMeta | undefined;
    /** 最近一次 hover 命中结果。 */
    private hoveredPick: THREE.Intersection<THREE.Object3D> | null = null;

    /**
     * 当前悬浮对象。
     * 供外部策略读取，例如捕捉或高亮逻辑。
     */
    public get hoveredObject(): THREE.Object3D | null {
        return this.hoveredPick?.object ?? null;
    }

    /**
     * 使用绘制态同源屏幕空间规则拾取当前鼠标下的活动草图边。
     *
     * 该入口只返回命中对象，不写入 `hoveredPick`、选择状态或交互材质。活动拖拽会短路普通
     * hover 更新，因此点捕捉通过这里复用 `ScreenSpacePrecisionPicker.pickNearestLine()`，保证
     * 拖动与绘制使用相同的可见性、实际线宽和草图线基础拾取半径。
     *
     * @param clientX 浏览器视口 X 坐标。
     * @param clientY 浏览器视口 Y 坐标。
     * @param excludedTargetObjectIds 当前移动域中不得命中的对象 ID 集合。
     * @returns 当前精确命中的活动草图边；未命中时返回 null。
     */
    public pickActiveSketchPointSnapEdgeAt(
        clientX: number,
        clientY: number,
        excludedTargetObjectIds?: ReadonlySet<string>,
    ): THREE.Object3D | null {
        const container = this.renderer.container;
        const camera = this.renderer.camera;
        const activeSketchGroup = this.renderer.sketchGroupManager?.activeSketchGroup;
        if (!container || !camera || !activeSketchGroup) {
            return null;
        }

        const rect = container.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) {
            return null;
        }

        this.pointSnapEdgeCandidates.length = 0;
        activeSketchGroup.getSketchEdgeObjects().forEach((edge) => {
            if (!excludedTargetObjectIds?.has(edge.uuid)) {
                this.pointSnapEdgeCandidates.push(edge as unknown as THREE.Line);
            }
        });
        if (this.pointSnapEdgeCandidates.length === 0) {
            return null;
        }

        // 与普通选择查询保持相同的 client → NDC 射线构造，再交给同一屏幕空间精确拾取器。
        this.mouseNdc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
        this.mouseNdc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
        this.raycaster.setFromCamera(this.mouseNdc, camera);
        const hit = this.screenSpacePrecisionPicker.pickNearestLine({
            lines: this.pointSnapEdgeCandidates,
            camera,
            containerRect: rect,
            clientX,
            clientY,
            ray: this.raycaster.ray,
        });
        return hit?.object ?? null;
    }
    /** 当前选中实体列表。 */
    private selectedPicks: THREE.Intersection<THREE.Object3D>[] = [];
    /** 外部视觉高亮仍需保留的 imported topology 临时代理。*/
    private externalRetainedImportedTopologyProxyObjects: THREE.Object3D[] = [];
    /**
     * 命令态高亮仍需要保留的 imported 拓扑代理。
     *
     * 说明：普通 selectedPicks 只表达最后一次场景点击，Mate 这类 PropertyManager
     * 会在独立命令通道中同时高亮多个 FeatureFace/FeatureLine/FeaturePoint。
     * 这些对象不应进入普通选择状态，但必须参与 imported pick host 的代理保留。
     */
    private commandSelectedTopologyProxyObjects: THREE.Object3D[] = [];
    /** 鼠标按下起点。 */
    private mouseDownStart: THREE.Vector2 | null = null;
    /** 当前是否处于框选拖拽流程。 */
    private isBoxDragging: boolean = false;
    /** 框选拖拽终点。 */
    private boxDragEnd: THREE.Vector2 | null = null;
    /** 框选框 DOM 元素。 */
    private selectionBoxElement: HTMLDivElement | null = null;
    /** 当前激活的拖拽策略实例。 */
    /** 当前选择模式。 */
    private selectionMode: EntitySelectionMode = 'object';
    /** 当前命令是否允许活动草图之外的草图实体参与选择候选。 */
    private allowExternalSketchEntities: boolean = false;
    /** 是否允许穿透前景面继续选择被遮挡的实体拓扑。 */
    private allowOccludedFeatureTopologySelection: boolean = false;
    /** 当前激活的拖拽策略实例。 */
    private activeDragStrategy: AbstractEntityDragStrategy | null = null;
    /** 是否需要忽略下一次 click 事件。 */
    private suppressNextClick: boolean = false;
    /** 默认模式处理器（当前复用装配体处理器实现）。 */
    private readonly defaultSelectionHandler: AssemblyEntitySelectionHandler;
    /** 装配体模式处理器。 */
    private readonly assemblySelectionHandler: AssemblyEntitySelectionHandler;
    /** 移动零部件命令模式处理器。 */
    private readonly moveComponentSelectionHandler: MoveComponentEntitySelectionHandler;
    //------ 线性零部件阵列 开始------
    /** 线性零部件阵列命令模式处理器。 */
    private readonly linearComponentPatternSelectionHandler: LinearComponentPatternEntitySelectionHandler;
    //------ 线性零部件阵列 结束------
    /** 草图模式处理器。 */
    private readonly sketchSelectionHandler: SketchEntitySelectionHandler;
    /** 基准面模式处理器。 */
    private readonly referencePlaneSelectionHandler: ReferencePlaneEntitySelectionHandler;
    /** 基准轴模式处理器。 */
    private readonly referenceAxisSelectionHandler: ReferenceAxisEntitySelectionHandler;
    /** 基准点模式处理器。 */
    private readonly referencePointSelectionHandler: ReferencePointEntitySelectionHandler;
    //------ 装配体模式下基准点线面 开始------
    /** 装配体模式下基准面处理器。 */
    private readonly assemblyReferencePlaneSelectionHandler: AssemblyReferencePlaneEntitySelectionHandler;
    /** 装配体模式下基准轴处理器。 */
    private readonly assemblyReferenceAxisSelectionHandler: AssemblyReferenceAxisEntitySelectionHandler;
    /** 装配体模式下基准点处理器。 */
    private readonly assemblyReferencePointSelectionHandler: AssemblyReferencePointEntitySelectionHandler;
    //------ 装配体模式下基准点线面 结束------
    /** 草图移动实体模式处理器。 */
    private readonly sketchMoveEntitiesSelectionHandler: SketchMoveEntitiesSelectionHandler;
    /** 草图复制实体模式处理器。 */
    private readonly sketchCopyEntitiesSelectionHandler: SketchCopyEntitiesSelectionHandler;
    /** 草图旋转实体模式处理器。 */
    private readonly sketchRotateEntitiesSelectionHandler: SketchRotateEntitiesSelectionHandler;
    /** 圆周草图阵列模式处理器。 */
    private readonly sketchCircularPatternSelectionHandler: SketchCircularPatternSelectionHandler;
    /** 草图镜向实体模式处理器。 */
    private readonly sketchMirrorEntitiesSelectionHandler: SketchMirrorEntitiesSelectionHandler;
    /** 裁剪模式处理器。 */
    private readonly sketchTrimSelectionHandler: SketchTrimEntitySelectionHandler;
    /** 智能尺寸模式处理器。 */
    private readonly sketchDimensionSelectionHandler: SketchDimensionEntitySelectionHandler;
    /** 等距实体处理器 */
    private readonly sketchOffsetSelectionHandler: SketchOffsetEntitySelectionHandler;
    /** 测量叠加层处理器。 */
    private readonly measureSelectionHandler: MeasureEntitySelectionHandler;
    /** 测量拾取候选集解析器。 */
    private readonly measurePickScopeResolver: MeasurePickScopeResolver;
    /** 当前激活的选择处理器（由 setMode 直接赋値，避免每次事件运行时解析）。 */
    private activeSelectionHandler!: AbstractEntitySelectionHandler;
    /** 在创建处登记所有权，保证非活动模式和后续新增处理器也有对称释放入口。 */
    private readonly ownedSelectionHandlers = new Set<AbstractEntitySelectionHandler>();
    /** 实体交互事件流集合。 */
    private readonly eventStreams: {
        [K in PuppyCAD.EntityInteractionEventType]: Subject<EntityInteractionEventPayloadMap[K]>
    };
    /** 原始事件订阅集合。 */
    private readonly rawPointerSubscriptions: Subscription[] = [];
    /** 最新悬停坐标的唯一所有者，由 Viewer 既有帧循环消费。 */
    private readonly hoverInputScheduler = new EntityHoverInputScheduler();
    /** 点击、Hover 与框选共用的稳定场景候选类型索引。 */
    private readonly selectionCandidateIndex: EntitySelectionCandidateIndex | null;
    /** 点、线、面候选的一次遍历稳定类型分区器。 */
    public readonly entitySelectionQueryPartitioner = new EntitySelectionQueryPartitioner();
    /**
     * 特征预览替换源的实时屏蔽规则。
     *
     * 该箭头函数在 Manager 生命周期内只创建一次，供每帧分区直接复用。
     * @param object 当前特征候选。
     * @returns 对象模式下已由预览结果替换时返回 true。
     */
    public readonly isModelBodyPreviewObjectPickBlocked = (object: THREE.Object3D): boolean => (
        this.selectionMode === 'object'
        && resolveModelBodySceneRegistry(this.renderer.scene).isPreviewReplacementSourceObject(object)
    );
    /** 点击与 Hover 热路径复用的候选输出缓冲。 */
    private readonly pointingSelectionTargetBuffer: THREE.Object3D[] = [];
    /** 框选热路径复用的候选输出缓冲。 */
    private readonly boxSelectionTargetBuffer: THREE.Object3D[] = [];

    /**
     * 构造函数。
     * @param renderer Viewer 实例。
     * @param sceneMembershipSource Viewer 统一场景成员源；缺失时保留旧完整遍历兼容路径。
     */
    constructor(renderer: Viewer, sceneMembershipSource?: SceneObjectMembershipSource) {
        this.eventManager = renderer.eventManager!;
        this.renderer = renderer;
        this.selectionCandidateIndex = sceneMembershipSource
            ? new EntitySelectionCandidateIndex(sceneMembershipSource)
            : null;
        //------ 装配体模式 开始------
        /**
         * 说明：
         * 1. 默认模式与装配体模式当前统一复用 AssemblyEntitySelectionHandler；
         * 2. 二者仍持有独立实例，仅通过配置决定是否启用装配体专属交互；
         * 3. 这样既保留模式边界，也删除了重复的 imported-model handler 类。
         */
        //------ 装配体模式 结束------
        this.defaultSelectionHandler = this.ownSelectionHandler(new AssemblyEntitySelectionHandler(
            this,
            new ImportedModelRaycastResolver(renderer),
            { assemblyModeEnabled: false },
        ));
        //------ 装配体模式 开始------
        this.assemblySelectionHandler = this.ownSelectionHandler(new AssemblyEntitySelectionHandler(
            this,
            new ImportedModelRaycastResolver(renderer),
            { assemblyModeEnabled: true },
        ));
        this.moveComponentSelectionHandler = this.ownSelectionHandler(new MoveComponentEntitySelectionHandler(
            this,
            new ImportedModelRaycastResolver(renderer),
            { assemblyModeEnabled: true },
        ));
        //------ 线性零部件阵列 开始------
        this.linearComponentPatternSelectionHandler = this.ownSelectionHandler(new LinearComponentPatternEntitySelectionHandler(
            this,
            new ImportedModelRaycastResolver(renderer),
            { assemblyModeEnabled: true },
        ));
        //------ 线性零部件阵列 结束------
        //------ 装配体模式 结束------
        this.sketchSelectionHandler = this.ownSelectionHandler(new SketchEntitySelectionHandler(this));
        /**
         * 普通基准命令使用各自独立处理器。
         *
         * 说明：
         * 1. 普通基准面 / 轴 / 点保持旧版 DefaultEntitySelectionHandler 继承链；
         * 2. 装配体基准面 / 轴 / 点的 imported-model 代理拾取已下沉到 AssemblyReference* 处理器；
         * 3. 这样普通基准命令与装配体基准命令可以各自演进，不再共享同一个选择实现。
         */
        this.referencePlaneSelectionHandler = this.ownSelectionHandler(new ReferencePlaneEntitySelectionHandler(
            this,
        ));
        this.referenceAxisSelectionHandler = this.ownSelectionHandler(new ReferenceAxisEntitySelectionHandler(
            this,
        ));
        this.referencePointSelectionHandler = this.ownSelectionHandler(new ReferencePointEntitySelectionHandler(
            this,
        ));
        //------ 装配体模式下基准点线面 开始------
        /**
         * 装配体基准点 / 轴 / 面使用独立处理器实例承接模式语义。
         *
         * 说明：
         * 1. 普通基准命令仍保留原处理器；
         * 2. 装配体基准命令复用 imported-model 代理拾取链；
         * 3. 后续装配体专属限制可在这些处理器内扩展，不污染普通基准命令。
         */
        this.assemblyReferencePlaneSelectionHandler = this.ownSelectionHandler(new AssemblyReferencePlaneEntitySelectionHandler(
            this,
            new ImportedModelRaycastResolver(renderer),
        ));
        this.assemblyReferenceAxisSelectionHandler = this.ownSelectionHandler(new AssemblyReferenceAxisEntitySelectionHandler(
            this,
            new ImportedModelRaycastResolver(renderer),
        ));
        this.assemblyReferencePointSelectionHandler = this.ownSelectionHandler(new AssemblyReferencePointEntitySelectionHandler(
            this,
            new ImportedModelRaycastResolver(renderer),
        ));
        //------ 装配体模式下基准点线面 结束------
        this.sketchMoveEntitiesSelectionHandler = this.ownSelectionHandler(new SketchMoveEntitiesSelectionHandler(this));
        this.sketchCopyEntitiesSelectionHandler = this.ownSelectionHandler(new SketchCopyEntitiesSelectionHandler(this));
        this.sketchRotateEntitiesSelectionHandler = this.ownSelectionHandler(new SketchRotateEntitiesSelectionHandler(this));
        this.sketchCircularPatternSelectionHandler = this.ownSelectionHandler(new SketchCircularPatternSelectionHandler(this));
        this.sketchMirrorEntitiesSelectionHandler = this.ownSelectionHandler(new SketchMirrorEntitiesSelectionHandler(this));
        this.sketchTrimSelectionHandler = this.ownSelectionHandler(new SketchTrimEntitySelectionHandler(this));
        this.sketchDimensionSelectionHandler = this.ownSelectionHandler(new SketchDimensionEntitySelectionHandler(this));
        this.sketchOffsetSelectionHandler = this.ownSelectionHandler(new SketchOffsetEntitySelectionHandler(this));
        this.measurePickScopeResolver = new MeasurePickScopeResolver({
            defaultModeSelectionHandler: this.defaultSelectionHandler,
            assemblySelectionHandler: this.assemblySelectionHandler,
            sketchSelectionHandler: this.sketchSelectionHandler,
            referencePlaneSelectionHandler: this.referencePlaneSelectionHandler,
            referenceAxisSelectionHandler: this.referenceAxisSelectionHandler,
            referencePointSelectionHandler: this.referencePointSelectionHandler,
            assemblyReferencePlaneSelectionHandler: this.assemblyReferencePlaneSelectionHandler,
            assemblyReferenceAxisSelectionHandler: this.assemblyReferenceAxisSelectionHandler,
            assemblyReferencePointSelectionHandler: this.assemblyReferencePointSelectionHandler,
        });
        this.measureSelectionHandler = this.ownSelectionHandler(new MeasureEntitySelectionHandler(
            this,
            renderer.measureController,
            this.measurePickScopeResolver,
        ));
        // 初始默认使用默认模式处理器。
        this.activeSelectionHandler = this.defaultSelectionHandler;
        this.eventStreams = {
            [EntityInteractionEventType.EntityHoverEnter]: new Subject<EntityInteractionEventPayloadMap[typeof EntityInteractionEventType.EntityHoverEnter]>(),
            [EntityInteractionEventType.EntityHoverLeave]: new Subject<EntityInteractionEventPayloadMap[typeof EntityInteractionEventType.EntityHoverLeave]>(),
            [EntityInteractionEventType.EntitySelected]: new Subject<EntityInteractionEventPayloadMap[typeof EntityInteractionEventType.EntitySelected]>(),
            //------ 特征实体选择事件 开始------
            [EntityInteractionEventType.FeatureFaceSelected]: new Subject<EntityInteractionEventPayloadMap[typeof EntityInteractionEventType.FeatureFaceSelected]>(),
            [EntityInteractionEventType.FeatureLineSelected]: new Subject<EntityInteractionEventPayloadMap[typeof EntityInteractionEventType.FeatureLineSelected]>(),
            [EntityInteractionEventType.FeaturePointSelected]: new Subject<EntityInteractionEventPayloadMap[typeof EntityInteractionEventType.FeaturePointSelected]>(),
            //------ 特征实体选择事件 结束------
            [EntityInteractionEventType.EntityBoxSelected]: new Subject<EntityInteractionEventPayloadMap[typeof EntityInteractionEventType.EntityBoxSelected]>(),
            [EntityInteractionEventType.PointSelected]: new Subject<EntityInteractionEventPayloadMap[typeof EntityInteractionEventType.PointSelected]>(),
            [EntityInteractionEventType.LineSelected]: new Subject<EntityInteractionEventPayloadMap[typeof EntityInteractionEventType.LineSelected]>(),
            [EntityInteractionEventType.CircleSelected]: new Subject<EntityInteractionEventPayloadMap[typeof EntityInteractionEventType.CircleSelected]>(),
            [EntityInteractionEventType.ArcSelected]: new Subject<EntityInteractionEventPayloadMap[typeof EntityInteractionEventType.ArcSelected]>(),
            [EntityInteractionEventType.EllipseSelected]: new Subject<EntityInteractionEventPayloadMap[typeof EntityInteractionEventType.EllipseSelected]>(),
            [EntityInteractionEventType.SplineSelected]: new Subject<EntityInteractionEventPayloadMap[typeof EntityInteractionEventType.SplineSelected]>(),
            [EntityInteractionEventType.StyleSplineSelected]: new Subject<EntityInteractionEventPayloadMap[typeof EntityInteractionEventType.StyleSplineSelected]>(),
            [EntityInteractionEventType.SlotSelected]: new Subject<EntityInteractionEventPayloadMap[typeof EntityInteractionEventType.SlotSelected]>(),
            [EntityInteractionEventType.SelectionCleared]: new Subject<EntityInteractionEventPayloadMap[typeof EntityInteractionEventType.SelectionCleared]>(),
            [EntityInteractionEventType.SelectionUpdated]: new Subject<EntityInteractionEventPayloadMap[typeof EntityInteractionEventType.SelectionUpdated]>(),
            [EntityInteractionEventType.MultipleEntitiesBoxSelected]: new Subject<EntityInteractionEventPayloadMap[typeof EntityInteractionEventType.MultipleEntitiesBoxSelected]>(),
        };
    }

    /**
     * 接管本管理器创建的选择处理器，保持具体类型并登记其销毁责任。
     *
     * @param handler 已构造完成的模式处理器。
     * @returns 原处理器；不改变事件路由或模式行为。
     */
    private ownSelectionHandler<T extends AbstractEntitySelectionHandler>(handler: T): T {
        this.ownedSelectionHandlers.add(handler);
        return handler;
    }

    /**
     * 配置裁剪模式允许选择的实体类型。
     *
     * 该入口只负责把领域层提供的准入规则注入裁剪选择处理器，
     * 不在选择中心内解释 Corner、Inside 或 Outside 等具体业务语义。
     *
     * @param predicate 实体准入判定器。
     */
    public setSketchTrimSelectableEntityPredicate(
        predicate: (object: THREE.Object3D) => boolean,
    ): void {
        this.sketchTrimSelectionHandler.setSelectableEntityPredicate(predicate);
    }

    /**
     * 启动事件桥接。
     */
    start(): void {
        // 已启动时不重复订阅。
        if (this.isStarted) {
            return;
        }

        // 订阅原始鼠标事件并转换为实体语义事件。
        this.rawPointerSubscriptions.push(
            this.eventManager.observe(RawPointerEventType.MouseDown).subscribe(this.handleMouseDown),
            this.eventManager.observe(RawPointerEventType.MouseMove).subscribe(this.handleMouseMove),
            this.eventManager.observe(RawPointerEventType.MouseUp).subscribe(this.handleMouseUp),
            this.eventManager.observe(RawPointerEventType.PointerCancel).subscribe(this.handlePointerCancel),
            this.eventManager.observe(RawPointerEventType.Click).subscribe(this.handleClick),
            this.eventManager.observe(RawPointerEventType.DoubleClick).subscribe(this.handleDoubleClick),
            this.eventManager.observePointerContextInvalidated().subscribe(this.handlePointerContextInvalidated),
        );

        this.isStarted = true;
    }

    /**
     * 停止事件桥接。
     */
    stop(): void {
        this.hoverInputScheduler.cancel();
        // 未启动时无需重复取消订阅。
        if (!this.isStarted) {
            return;
        }

        // 对称移除原始事件订阅，避免残留回调。
        this.rawPointerSubscriptions.forEach((subscription) => subscription.unsubscribe());
        this.rawPointerSubscriptions.length = 0;

        // 若仍处于拖拽中，先停止策略，避免场景残留。
        this.stopDragInternal();

        // 重置内部状态，避免跨会话污染。
        this.hoveredPick = null;
        this.selectedPicks = [];
        this.externalRetainedImportedTopologyProxyObjects = [];
        this.commandSelectedTopologyProxyObjects = [];
        this.clearCurrentSelectedEntitiesInternal();
        this.mouseDownStart = null;
        this.isBoxDragging = false;
        this.boxDragEnd = null;
        this.activeDragStrategy = null;
        this.resetSelectionBoxVisualInternal();
        this.suppressNextClick = false;
        this.selectionMode = 'object';
        this.allowExternalSketchEntities = false;
        this.allowOccludedFeatureTopologySelection = false;
        this.importedTopologyProxyRetentionController.clear();
        this.isStarted = false;
    }

    /**
     * 同步导入拓扑临时代理的保留状态。
     */
    public syncImportedTopologyProxyRetention(): void {
        this.importedTopologyProxyRetentionController.sync({
            hoveredPick: this.hoveredPick,
            selectedPicks: this.selectedPicks,
            retainedObjects: [
                ...this.externalRetainedImportedTopologyProxyObjects,
                ...this.commandSelectedTopologyProxyObjects,
            ],
            additionalRetainedObjects: this.resolveMeasureRetainedTopologyObjects(),
        });
    }

    /**
     * 写入外部视觉链路仍需保留的 imported topology 代理。
     *
     * 说明：
     * Convert Entities 会主动清空真实 Selected 状态，但来源高亮仍需要显示模型边线；
     * 因此这里把视觉高亮对象纳入统一临时代理保留链路，避免边代理在清空选择后被回收。
     *
     * @param objects 当前外部视觉链路引用的运行时对象。
     */
    public setExternalImportedTopologyProxyRetainers(objects: readonly THREE.Object3D[]): void {
        this.externalRetainedImportedTopologyProxyObjects = [...objects];
        this.syncImportedTopologyProxyRetention();
    }

    /**
     * 设置命令态高亮需要保留的 imported 拓扑代理对象。
     *
     * @param objects 命令态高亮仍在引用的 FeatureFace / FeatureLine / FeaturePoint 对象。
     */
    public setCommandSelectedTopologyProxyObjects(objects: readonly THREE.Object3D[]): void {
        this.commandSelectedTopologyProxyObjects = [...objects];
        this.syncImportedTopologyProxyRetention();
    }

    /**
     * 读取当前命令态接管的拓扑代理对象。
     *
     * 设计说明：
     * Mate 等命令会通过 imported-model overlay 持续高亮多个 FeatureFace / FeatureLine / FeaturePoint。
     * 交互视觉状态需要只读访问这份列表，用于阻止普通 selected/hover 材质再次接管这些代理；
     * 返回副本可避免外部代码直接修改选择管理器内部状态。
     *
     * @returns 命令态仍需保留并高亮的对象副本。
     */
    public getCommandSelectedTopologyProxyObjects(): THREE.Object3D[] {
        return [...this.commandSelectedTopologyProxyObjects];
    }

    /**
     * 清空命令态高亮的 imported 拓扑代理保留列表。
     */
    public clearCommandSelectedTopologyProxyObjects(): void {
        if (this.commandSelectedTopologyProxyObjects.length === 0) {
            return;
        }
        this.commandSelectedTopologyProxyObjects = [];
        this.syncImportedTopologyProxyRetention();
    }

    /**
     * 解析测量会话中仍需保留的 imported 拓扑代理对象。
     *
     * 设计说明：
     * 1. 测量选中集与命令选中集隔离，若不纳入保留列表，hover 离开时会误回收测量选中代理；
     * 2. 仅测量会话激活时返回对象，避免影响普通命令态代理回收语义。
     *
     * @returns 测量选中对应的场景对象列表。
     */
    private resolveMeasureRetainedTopologyObjects(): THREE.Object3D[] {
        if (!this.renderer.measureController.isMeasureActive()) {
            return [];
        }

        const retainedObjects: THREE.Object3D[] = [];
        this.renderer.measureController.getSelectedEntities().forEach((entity) => {
            const sceneObject = this.resolveSceneObjectById(entity.objectId);
            if (sceneObject) {
                retainedObjects.push(sceneObject);
            }
        });
        return retainedObjects;
    }

    /**
     * 清空导入拓扑临时代理的全部保留状态。
     */
    public clearImportedTopologyProxyRetention(): void {
        this.externalRetainedImportedTopologyProxyObjects = [];
        this.commandSelectedTopologyProxyObjects = [];
        this.importedTopologyProxyRetentionController.clear();
    }

    /**
     * 读取 imported topology pick host 有界缓存统计。
     *
     * @returns 当前活动/空闲宿主及复用、淘汰计数。
     */
    public getImportedTopologyProxyRetentionStats(): ImportedModelTopologyProxyRetentionStats {
        return this.importedTopologyProxyRetentionController.getStats();
    }

    /**
     * 获取指定实体交互事件类型的可观察事件流。
     * @param type 事件类型。
     * @returns 对应事件流。
     */
    observe<T extends PuppyCAD.EntityInteractionEventType>(type: T): Observable<EntityInteractionEventPayloadMap[T]> {
        return this.eventStreams[type].asObservable() as Observable<EntityInteractionEventPayloadMap[T]>;
    }

    /**
     * 获取当前选中的实体对象列表。
     * @returns 当前选中的 Object3D 列表副本。
     */
    getSelectedObjects(): THREE.Object3D[] {
        return this.selectedPicks.map((pick) => pick.object);
    }

    /**
     * 获取当前选中命中快照。
     * @returns 选中命中列表。
     */
    public getSelectedPicks(): THREE.Intersection<THREE.Object3D>[] {
        return [...this.selectedPicks];
    }

    /**
     * 获取当前悬停命中快照。
     * @returns 当前悬停命中；不存在时返回 null。
     */
    public getHoveredPick(): THREE.Intersection<THREE.Object3D> | null {
        return this.hoveredPick;
    }

    /**
     * 写入当前悬停命中快照。
     * @param pick 最新悬停命中。
     */
    public setHoveredPick(pick: THREE.Intersection<THREE.Object3D> | null): void {
        this.hoveredPick = pick;
        this.syncImportedTopologyProxyRetention();
    }

    /**
     * 判断指定对象当前是否已被选中。
     * @param object 待判断对象。
     * @returns 是否已选中。
     */
    public isObjectSelected(object: THREE.Object3D): boolean {
        return this.selectedPicks.some((pick) => pick.object === object);
    }

    /**
     * 判断当前是否启用 hover 调试。
     * @returns 是否启用 hover 调试。
     */
    public isHoverDebugEnabled(): boolean {
        return EntitySelectionManager.debug.hover;
    }

    /**
     * 判断当前是否启用框选调试。
     * @returns 是否启用框选调试。
     */
    public isBoxSelectDebugEnabled(): boolean {
        return EntitySelectionManager.debug.boxSelect;
    }

    /**
     * 设置是否启用框选调试。
     *
     * 说明：
     * 1. 该属性默认关闭，避免普通框选时显示对象屏幕包围盒调试层；
     * 2. 开启后会同时输出框选对象日志，并显示候选对象的屏幕包围盒尺寸；
     * 3. 仅影响调试可视化与日志，不改变框选命中规则。
     *
     * @param enabled 是否启用框选调试。
     */
    public setBoxSelectDebugEnabled(enabled: boolean): void {
        EntitySelectionManager.debug.boxSelect = enabled;
    }

    /**
     * 发布实体移入事件。
     * @param object 命中的对象。
     * @param interactionMeta
     */
    public publishEntityHoverEnter(
        object: THREE.Object3D,
        interactionMeta: SelectionInteractionMeta | undefined = this.currentHoverInteractionMeta,
    ): void {
        this.publishInteractionEvent(EntityInteractionEventType.EntityHoverEnter, {
            type: EntityInteractionEventType.EntityHoverEnter,
            objectId: object.uuid,
            object,
            interactionMeta,
            timestamp: Date.now(),
        });
    }

    /**
     * 发布实体移出事件。
     * @param object 移出的对象。
     * @param interactionMeta
     */
    public publishEntityHoverLeave(
        object: THREE.Object3D,
        interactionMeta: SelectionInteractionMeta | undefined = this.currentHoverInteractionMeta,
    ): void {
        this.publishInteractionEvent(EntityInteractionEventType.EntityHoverLeave, {
            type: EntityInteractionEventType.EntityHoverLeave,
            objectId: object.uuid,
            object,
            interactionMeta,
            timestamp: Date.now(),
        });
    }

    /**
     * 获取当前选中的实体 ID 列表。
     * @returns 当前选中的 uuid 列表副本。
     */
    getSelectedObjectIds(): string[] {
        return this.selectedPicks.map((pick) => pick.object.uuid);
    }

    /**
     * 判断对象是否属于指定对象树。
     *
     * 设计说明：
     * 1. 结果体替换时，被移除的不只是根 mesh，自身的边/面/点代理也会一起失效；
     * 2. 选择层不能只按根对象 ID 比较，否则旧代理对象仍会残留在 selected / hovered 状态里；
     * 3. 因此这里统一以“根对象或其任意后代”作为判定范围，供对象树移除时收口交互状态。
     *
     * @param root 被移除的对象树根节点。
     * @param target 待判断对象。
     * @returns 是否属于该对象树。
     */
    private isObjectInsideTree(root: THREE.Object3D, target: THREE.Object3D): boolean {
        if (root === target || root.getObjectByProperty('uuid', target.uuid) !== undefined) {
            return true;
        }
        return resolveOwningFeatureResultMesh(target) === root;
    }

    /**
     * 设置当前选择模式。
     *
     * @param mode 目标模式。
     * @param options 选项。
     */
    public setSelectionMode(
        mode: EntitySelectionMode,
        options: EntitySelectionModeOptions = {},
    ): void {
        const nextMode = normalizeEntitySelectionMode(mode);
        const preserveSelection = options.preserveSelection === true;
        const nextAllowExternalSketchEntities = options.allowExternalSketchEntities === true;
        const selectionContextUnchanged = this.selectionMode === nextMode
            && this.allowExternalSketchEntities === nextAllowExternalSketchEntities;
        if (selectionContextUnchanged) {
            return;
        }

        this.selectionMode = nextMode;
        this.handlePointerContextInvalidated();
        this.allowExternalSketchEntities = nextAllowExternalSketchEntities;
        if (preserveSelection) {
            const filteredPicks = this.selectedPicks.filter((pick) => (
                SelectionQueryHelpers.isObjectAllowedInSelectionMode(pick.object, nextMode)
                && this.isSketchObjectSelectable(pick.object)
            ));
            SelectionStateHelpers.updateSelectionState(this as unknown as EntitySelectionManagerRuntime, filteredPicks);
            return;
        }

        SelectionStateHelpers.updateSelectionState(this as unknown as EntitySelectionManagerRuntime, []);
    }

    /**
     * 获取当前选择模式。
     *
     * @returns 当前模式。
     */
    public getSelectionMode(): EntitySelectionMode {
        return this.selectionMode;
    }

    /**
     * 判断对象是否允许进入当前草图选择候选集。
     *
     * 设计说明：
     * 1. 活动草图身份与命令临时范围都由选择管理器维护，调用方无需拼装判断上下文；
     * 2. 该入口只判断选择候选资格，不改变拖拽与几何编辑权限；
     * 3. 非草图对象继续由底层守卫透传，再交给选择模式完成类型裁剪。
     *
     * @param targetObject 待判断的场景对象。
     * @returns 对象允许参与当前草图选择时返回 `true`。
     */
    public isSketchObjectSelectable(targetObject: THREE.Object3D | null | undefined): boolean {
        return isSketchObjectSelectableInActiveSketch({
            activeSketchGroupId:
                this.renderer.sketchGroupManager?.activeSketchGroup?.uuid ?? null,
            targetObject,
            allowExternalSketchEntities: this.allowExternalSketchEntities,
        });
    }

    /**
     * 清空当前选择与悬浮状态。
     *
     * 设计说明：
     * 1. 结果体全量重建后，旧场景对象会立即失效；
     * 2. 若底层仍保留这些旧对象的选中/悬浮引用，后续特征命令会把离场对象误当成当前输入；
     * 3. 因此这里统一清空 hover 与 selected 两条状态链，保证后续命令从干净环境启动。
     */
    public clearSelection(): void {
        this.hoverInputScheduler.cancel();
        SelectionStateHelpers.clearHoverState(this as unknown as EntitySelectionManagerRuntime);
        SelectionStateHelpers.updateSelectionState(this as unknown as EntitySelectionManagerRuntime, []);
    }

    /**
     * 从当前交互状态中移除指定对象树。
     *
     * 设计说明：
     * 1. 结果体增量替换不会走全量 `syncFeatureMeshesFromState()`，因此不能依赖全局 `clearSelection()` 兜底；
     * 2. 旧结果体离场时，若 selection manager 仍保留其边/面/点代理引用，后续圆角/倒角会错误吸收旧实体；
     * 3. 这里把“对象树移除”和“交互状态剔除”绑定为同一条底层链路，保证任何结果体替换都不会遗留陈旧选择。
     *
     * @param root 即将离场的对象树根节点。
     */
    public removeObjectTreeFromSelection(root: THREE.Object3D): void {
        this.invalidateHoverPick();
        const hoveredPickBelongsToTree = this.hoveredPick
            ? this.isObjectInsideTree(root, this.hoveredPick.object)
            : false;
        if (hoveredPickBelongsToTree) {
            SelectionStateHelpers.clearHoverState(this as unknown as EntitySelectionManagerRuntime);
        }

        const nextSelectedPicks = this.selectedPicks.filter((pick) => !this.isObjectInsideTree(root, pick.object));
        if (nextSelectedPicks.length === this.selectedPicks.length) {
            return;
        }

        SelectionStateHelpers.updateSelectionState(
            this as unknown as EntitySelectionManagerRuntime,
            nextSelectedPicks,
        );
    }

    /**
     * 设置当前实体拓扑是否允许穿透前景面进行选择。
     *
     * @param value 是否允许穿透前景面选择被遮挡的边、面或点。
     */
    public setAllowOccludedFeatureTopologySelection(value: boolean): void {
        if (this.allowOccludedFeatureTopologySelection !== (value === true)) this.invalidateHoverPick();
        this.allowOccludedFeatureTopologySelection = value === true;
    }

    /**
     * 读取当前实体拓扑是否允许穿透前景面进行选择。
     *
     * @returns 是否允许穿透前景面选择被遮挡的实体拓扑。
     */
    public getAllowOccludedFeatureTopologySelection(): boolean {
        return this.allowOccludedFeatureTopologySelection;
    }

    /**
     * 获取当前主选对象。
     * @returns 主选对象；未命中时返回 null。
     */
    public getPrimarySelectedObject(): THREE.Object3D | null {
        return this.selectedPicks[0]?.object ?? null;
    }

    /**
     * 获取当前主选面的最小信息。
     * @returns 面信息；当前主选不是特征面时返回 null。
     */
    public getSelectedFaceInfo(): { objectId: string; faceIndex: number } | null {
        const selectedObject = this.getPrimarySelectedObject();
        if (!selectedObject || !isFeatureFace(selectedObject)) {
            return null;
        }
        return {
            objectId: selectedObject.objectId,
            faceIndex: selectedObject.faceIndex,
        };
    }

    /**
     * 在指定屏幕坐标拾取草图轮廓。
     * @param clientX 屏幕 X 坐标。
     * @param clientY 屏幕 Y 坐标。
     * @returns 命中的草图轮廓；未命中时返回 null。
     */
    public pickSketchContourAt(clientX: number, clientY: number): SketchContour | null {
        const container = this.renderer.container;
        const camera = this.renderer.camera;
        if (!container || !camera) {
            return null;
        }

        const contourCandidates: THREE.Object3D[] = [];
        //------ 活动草图候选过滤 开始------
        this.renderer.scene.traverse((object) => {
            if (!isSketchContour(object)) {
                return;
            }

            // 轮廓兜底拾取必须与草图 handler 的普通候选过滤保持一致，避免其它草图轮廓绕过收集层。
            if (!this.isSketchObjectSelectable(object)) {
                return;
            }

            contourCandidates.push(object);
        });
        //------ 活动草图候选过滤 结束------

        if (contourCandidates.length === 0) {
            return null;
        }

        const rect = container.getBoundingClientRect();
        this.mouseNdc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
        this.mouseNdc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
        this.raycaster.setFromCamera(this.mouseNdc, camera);

        const intersects = this.raycaster.intersectObjects(contourCandidates, true);
        const contourHit = intersects.find((item) => isSketchContour(item.object) || this.findContourAncestor(item.object));
        if (!contourHit) {
            return null;
        }

        if (isSketchContour(contourHit.object)) {
            return contourHit.object;
        }

        return this.findContourAncestor(contourHit.object);
    }

    /**
     * 以程序方式选中指定实体，并派发对应选中事件。
     * 用于绘制完成后自动选中新创建的实体。
     * @param entity 需要选中的实体对象。
     */
    selectEntityProgrammatically(entity: THREE.Object3D): void {
        this.updateSelectionState([{
            distance: 0,
            object: entity,
            point: entity.getWorldPosition(new THREE.Vector3()),
        }], undefined);
    }

    /**
     * 以对象 ID 触发程序化选择。
     * @param objectId 目标对象 ID。
     * @param options 选择选项。
     */
    public selectEntityByObjectId(
        objectId: string,
        options: { interactionMeta?: SelectionInteractionMeta } = {},
    ): void {
        const entity = this.resolveSceneObjectById(objectId, options.interactionMeta);
        if (!entity) {
            return;
        }

        const pick: THREE.Intersection<THREE.Object3D> = {
            distance: 0,
            object: entity,
            point: entity.getWorldPosition(new THREE.Vector3()),
        };

        if (this.routeProgrammaticSelectionToMeasure([pick], options.interactionMeta)) {
            return;
        }

        this.updateSelectionState([pick], options.interactionMeta);
    }

    //------ 线性零部件阵列节点选中高亮 开始------
    /**
     * 以对象 ID 列表触发程序化多目标选择。
     *
     * 设计说明：
     * 1. 线性零部件阵列分组节点代表一组实例，而不是单个场景对象；
     * 2. 这里复用选择中心已有的多选状态写入能力，保证装配体视觉状态继续从 `selectedObjects` 统一消费；
     * 3. 无法解析的对象 ID 会被跳过，避免单个失效实例阻断其余实例高亮。
     *
     * @param objectIds 目标对象 ID 列表。
     * @param options 选择选项。
     */
    public selectEntitiesByObjectIds(
        objectIds: readonly string[],
        options: { interactionMeta?: SelectionInteractionMeta } = {},
    ): void {
        const selectedPicks = objectIds
            .map((objectId) => this.resolveSceneObjectById(objectId, options.interactionMeta))
            .filter((entity): entity is THREE.Object3D => Boolean(entity))
            .map((entity) => ({
                distance: 0,
                object: entity,
                point: entity.getWorldPosition(new THREE.Vector3()),
            }));

        if (this.routeProgrammaticSelectionToMeasure(selectedPicks, options.interactionMeta)) {
            return;
        }

        this.updateSelectionState(selectedPicks, options.interactionMeta);
    }
    //------ 线性零部件阵列节点选中高亮 结束------

    /**
     * 以对象 ID 触发程序化悬停。
     * @param objectId 目标对象 ID；传入 null 时清空当前悬停。
     * @param options 悬停选项。
     */
    public setProgrammaticHover(
        objectId: string | null,
        options: { interactionMeta?: SelectionInteractionMeta } = {},
    ): void {
        this.hoverInputScheduler.cancel();
        if (!objectId) {
            //------ 设计树程序化特征移入高亮 开始------
            this.renderer.interactionStateManager?.setProgrammaticHoveredFeatureResultMesh(null, false);
            //------ 设计树程序化特征移入高亮 结束------
            //------ 线性零部件阵列节点包围盒移入 开始------
            this.renderer.interactionStateManager?.setProgrammaticHoveredObjects([], undefined, false);
            //------ 线性零部件阵列节点包围盒移入 结束------
            const previousHoveredPick = this.hoveredPick;
            this.hoveredPick = null;
            if (previousHoveredPick) {
                this.publishEntityHoverLeave(previousHoveredPick.object, this.currentHoverInteractionMeta);
            }
            this.currentHoverInteractionMeta = undefined;
            this.syncImportedTopologyProxyRetention();
            return;
        }

        const entity = this.resolveSceneObjectById(objectId, options.interactionMeta);
        if (!entity) {
            //------ 设计树程序化特征移入高亮 开始------
            this.renderer.interactionStateManager?.setProgrammaticHoveredFeatureResultMesh(null);
            //------ 设计树程序化特征移入高亮 结束------
            //------ 线性零部件阵列节点包围盒移入 开始------
            this.renderer.interactionStateManager?.setProgrammaticHoveredObjects([]);
            //------ 线性零部件阵列节点包围盒移入 结束------
            return;
        }
        //------ 设计树程序化特征移入高亮 开始------
        this.renderer.interactionStateManager?.setProgrammaticHoveredFeatureResultMesh(
            isFeatureResultMesh(entity) ? entity : null,
            false,
        );
        //------ 设计树程序化特征移入高亮 结束------
        //------ 线性零部件阵列节点包围盒移入 开始------
        this.renderer.interactionStateManager?.setProgrammaticHoveredObjects([], undefined, false);
        //------ 线性零部件阵列节点包围盒移入 结束------

        const previousHoveredPick = this.hoveredPick;
        if (previousHoveredPick && previousHoveredPick.object.uuid !== entity.uuid) {
            this.publishEntityHoverLeave(previousHoveredPick.object, this.currentHoverInteractionMeta);
        }

        this.hoveredPick = {
            distance: 0,
            object: entity,
            point: entity.getWorldPosition(new THREE.Vector3()),
        };
        this.currentHoverInteractionMeta = options.interactionMeta;
        this.publishEntityHoverEnter(entity, options.interactionMeta);
        this.syncImportedTopologyProxyRetention();
    }

    //------ 线性零部件阵列节点包围盒移入 开始------
    /**
     * 以对象 ID 列表触发程序化多目标悬停。
     *
     * 设计说明：
     * 1. 单对象 hover 仍由 `setProgrammaticHover` 负责，保持既有事件语义稳定；
     * 2. 线性零部件阵列分组需要同时显示多个子零件包围盒，不能压缩成一个合并框；
     * 3. 这里只解析场景对象并交给 InteractionStateManager，避免在选择中心创建渲染 helper。
     *
     * @param objectIds 目标对象 ID 列表；为空时清空当前多目标悬停。
     * @param options 悬停选项。
     */
    public setProgrammaticHoverTargets(
        objectIds: readonly string[],
        options: { interactionMeta?: SelectionInteractionMeta } = {},
    ): void {
        this.hoverInputScheduler.cancel();
        const entities = objectIds
            .map((objectId) => this.resolveSceneObjectById(objectId, options.interactionMeta))
            .filter((entity): entity is THREE.Object3D => Boolean(entity));

        const previousHoveredPick = this.hoveredPick;
        this.hoveredPick = null;
        if (previousHoveredPick) {
            this.publishEntityHoverLeave(previousHoveredPick.object, this.currentHoverInteractionMeta);
        }
        this.currentHoverInteractionMeta = undefined;
        this.renderer.interactionStateManager?.setProgrammaticHoveredFeatureResultMesh(null, false);
        this.renderer.interactionStateManager?.setProgrammaticHoveredObjects(
            entities,
            options.interactionMeta,
        );
        this.syncImportedTopologyProxyRetention();
    }
    //------ 线性零部件阵列节点包围盒移入 结束------

    /**
     * 以程序方式清空当前选择。
     *
     * @param options 清空范围；测量会话期间默认清空测量选中集。
     */
    public clearSelectionProgrammatically(
        options: { scope?: 'command' | 'auto'; intent?: SelectionUpdateIntent } = {},
    ): void {
        const scope = options.scope ?? 'auto';
        if (this.renderer.measureController.isMeasureActive()) {
            if (scope === 'command') {
                this.updateSelectionState([], undefined, undefined, { intent: options.intent });
                return;
            }
            this.renderer.measureController.clearSelectedEntities();
            this.updateSelectionState([], undefined, undefined, { intent: options.intent });
            return;
        }
        this.updateSelectionState([], undefined, undefined, { intent: options.intent });
    }

    /**
     * 测量会话期间将程序化选中路由到测量控制器。
     *
     * @param picks 程序化命中列表。
     * @param interactionMeta 选择交互元信息。
     * @returns 是否已由测量控制器接管。
     */
    private routeProgrammaticSelectionToMeasure(
        picks: readonly THREE.Intersection<THREE.Object3D>[],
        interactionMeta?: SelectionInteractionMeta,
    ): boolean {
        if (!this.renderer.measureController.isMeasureActive()) {
            return false;
        }

        const source = interactionMeta?.source === 'feature-tree'
            ? 'feature-tree'
            : 'programmatic';
        const accepted = this.renderer.measureController.applyProgrammaticPicks(picks, source);
        if (!accepted && picks.length > 0) {
            this.renderer.drawingNoticeCallback?.(
                '当前实体不支持测量',
                'warning',
            );
        }
        return true;
    }

    /**
     * 重置模型历史恢复前的临时交互状态。
     *
     * 说明：undo/redo 会替换草图运行态对象树，旧的 hover、框选和拖拽引用如果继续保留，
     * 后续鼠标事件会命中过期 Object3D，表现为绘制后无法继续点击或视图控制被吞掉。
     */
    public resetTransientInteractionStateForModelRestore(): void {
        this.hoverInputScheduler.cancel();
        if (this.hoveredPick) {
            this.publishEntityHoverLeave(this.hoveredPick.object, this.currentHoverInteractionMeta);
        }
        if (this.activeDragStrategy) {
            this.activeDragStrategy.stop();
            this.activeDragStrategy = null;
        }
        this.updateSelectionState([], undefined);
        this.hoveredPick = null;
        this.mouseDownStart = null;
        this.isBoxDragging = false;
        this.boxDragEnd = null;
        this.resetSelectionBoxVisualInternal();
        this.suppressNextClick = false;
        this.allowExternalSketchEntities = false;
        this.currentHoverInteractionMeta = undefined;
        this.currentSelectionInteractionMeta = undefined;
        /**
         * 模型恢复会替换运行时 Object3D 树，命令态拓扑代理也必须一并失效。
         * 只清 retention 不清列表会让下一次同步重新保留旧代理，造成命令高亮绑定过期对象。
         */
        this.commandSelectedTopologyProxyObjects = [];
        this.importedTopologyProxyRetentionController.clear();
    }

    /**
     * 更新当前选择状态并派发事件。
     * @param nextSelectedPicks 新选中命中集合。
     * @param interactionMeta 选择交互元信息。
     * @param selectionSourcePicks 本次选择的原始命中集合，用于保留比语义选中更细的来源对象。
     * @param options 选择状态更新选项。
     */
    public updateSelectionState(
        nextSelectedPicks: THREE.Intersection<THREE.Object3D>[],
        interactionMeta?: SelectionInteractionMeta,
        selectionSourcePicks?: THREE.Intersection<THREE.Object3D>[],
        options: SelectionUpdateOptions = {},
    ): void {
        const uniqueSelectedPicks = this.normalizeSelectionPicksByObject(nextSelectedPicks);
        const uniqueSelectionSourcePicks = selectionSourcePicks && selectionSourcePicks.length > 0
            ? this.normalizeSelectionPicksByObject(selectionSourcePicks)
            : uniqueSelectedPicks;
        const topologySelectionEntries = uniqueSelectedPicks
            .map((item) => ({
                object: item.object,
                topologyPick: resolveTopologySelectionPick(item.object),
            }))
            .filter((entry): entry is {
                object: THREE.Object3D;
                topologyPick: NonNullable<ReturnType<typeof resolveTopologySelectionPick>>;
            } => entry.topologyPick !== null);
        const topologyPicks = topologySelectionEntries.map((entry) => entry.topologyPick);
        const assemblyTopologySelectionEntries = topologySelectionEntries
            .map((entry) => {
                const assemblyTopologyRef = resolveImportedAssemblyTopologySelectionRef(entry.object, entry.topologyPick);
                const brepGeometryDescriptor = resolveBRepGeometryDescriptor(entry.object);
                if (!assemblyTopologyRef || !brepGeometryDescriptor) {
                    return null;
                }
                return {
                    objectId: entry.object.uuid,
                    topologyPick: entry.topologyPick,
                    assemblyTopologyRef,
                    brepGeometryDescriptor,
                };
            })
            .filter((entry): entry is AssemblyTopologySelectionSnapshotEntry => entry !== null);
        const assemblyTopologyRefs = assemblyTopologySelectionEntries
            .map((entry) => entry.assemblyTopologyRef);
        const brepGeometryDescriptors = assemblyTopologySelectionEntries
            .map((entry) => entry.brepGeometryDescriptor);
        const assemblyReferenceSelectionEntries = uniqueSelectedPicks
            .map((item) => resolveAssemblyReferenceSelectionSnapshotEntry(item.object, this.renderer))
            .filter((entry): entry is AssemblyReferenceSelectionSnapshotEntry => entry !== null);
        const assemblyReferenceSelectionRefs = assemblyReferenceSelectionEntries
            .map((entry) => entry.referenceRef);
        const picksChanged = !this.isPickArrayEqual(this.selectedPicks, uniqueSelectedPicks);
        const nextInteractionMeta = uniqueSelectedPicks.length > 0 ? interactionMeta : undefined;
        const interactionMetaChanged = !SelectionStateHelpers.areSelectionInteractionMetasEqual(
            this.currentSelectionInteractionMeta,
            nextInteractionMeta,
        );
        const changed = picksChanged || interactionMetaChanged;
        this.selectedPicks = uniqueSelectedPicks;
        this.currentSelectionInteractionMeta = nextInteractionMeta;

        this.clearHoverWhenSelectionConflictsInternal();
        this.syncCurrentSelectedEntitiesInternal(uniqueSelectedPicks);
        this.publishCurrentSelectionEventInternal();

        /**
         * 程序化选择与鼠标选择必须发布同一份结算快照。
         * Editor 侧只消费 SelectionUpdated 才能稳定拿到拓扑命中信息，不能让公开 API 继续停留在旧事件协议。
         */
        this.publishInteractionEvent(EntityInteractionEventType.SelectionUpdated, {
            type: EntityInteractionEventType.SelectionUpdated,
            objectId: uniqueSelectedPicks[0]?.object.uuid ?? null,
            object: uniqueSelectedPicks[0]?.object ?? null,
            selectionSourceObjectId: uniqueSelectionSourcePicks[0]?.object.uuid ?? null,
            selectionSourceObject: uniqueSelectionSourcePicks[0]?.object ?? null,
            selectedObjectIds: uniqueSelectedPicks.map((item) => item.object.uuid),
            selectedObjects: uniqueSelectedPicks.map((item) => item.object),
            selectionSourceObjectIds: uniqueSelectionSourcePicks.map((item) => item.object.uuid),
            selectionSourceObjects: uniqueSelectionSourcePicks.map((item) => item.object),
            topologyPick: topologyPicks[0] ?? null,
            topologyPicks,
            assemblyTopologyRef: assemblyTopologyRefs[0] ?? null,
            assemblyTopologyRefs,
            assemblyTopologySelectionEntries,
            assemblyReferenceSelectionRef: assemblyReferenceSelectionRefs[0] ?? null,
            assemblyReferenceSelectionRefs,
            assemblyReferenceSelectionEntries,
            brepGeometryDescriptor: brepGeometryDescriptors[0] ?? null,
            brepGeometryDescriptors,
            interactionMeta: this.currentSelectionInteractionMeta,
            selectionIntent: options.intent,
            timestamp: Date.now(),
        });

        this.syncImportedTopologyProxyRetention();

        if (!changed) {
            return;
        }

        if (this.shouldSuppressOrdinaryEntitySelectedForCommandTopology()) {
            this.publishEmptyEntitySelectedEvent();
            return;
        }

        this.publishInteractionEvent(EntityInteractionEventType.EntitySelected, {
            type: EntityInteractionEventType.EntitySelected,
            objectId: uniqueSelectedPicks[0]?.object.uuid ?? null,
            object: uniqueSelectedPicks[0]?.object ?? null,
            selectedObjectIds: uniqueSelectedPicks.map((item) => item.object.uuid),
            selectedObjects: uniqueSelectedPicks.map((item) => item.object),
            interactionMeta: this.currentSelectionInteractionMeta,
            timestamp: Date.now(),
        });
    }

    /**
     * 判断普通 EntitySelected 是否应由命令态拓扑高亮接管。
     *
     * 设计说明：
     * Mate 命令在 `SelectionUpdated` 期间已经登记完整的多实体高亮。
     * 如果这里继续发布最后一次点击对象，InteractionStateManager 会把该对象重新刷成普通 selected，
     * 造成用户看到单面高亮或幽灵蓝色面，因此必须在选择层统一收口。
     *
     * @returns 当前是否存在命令态拓扑代理高亮。
     */
    private shouldSuppressOrdinaryEntitySelectedForCommandTopology(): boolean {
        return this.commandSelectedTopologyProxyObjects.length > 0;
    }

    /**
     * 发布空普通选中事件。
     *
     * 说明：
     * `SelectionUpdated` 仍然携带真实命中供 Mate 吸收；这里只清空普通 selected 视觉通道，
     * 保证命令态 overlay 成为唯一可见选中表达。
     */
    private publishEmptyEntitySelectedEvent(): void {
        this.publishInteractionEvent(EntityInteractionEventType.EntitySelected, {
            type: EntityInteractionEventType.EntitySelected,
            objectId: null,
            object: null,
            selectedObjectIds: [],
            selectedObjects: [],
            interactionMeta: this.currentSelectionInteractionMeta,
            timestamp: Date.now(),
        });
    }

    /**
     * 根据对象 ID 解析场景对象。
     * @param objectId 目标对象 ID。
     * @returns 命中的场景对象；未命中时返回 null。
     */
    /**
     * 判断候选导入模型对象是否符合程序化选择作用域。
     *
     * @param object 候选场景对象。
     * @param interactionMeta 程序化选择元信息。
     * @returns 候选对象是否属于当前装配实例作用域。
     */
    private isImportedSelectionObjectInInteractionScope(
        object: THREE.Object3D,
        interactionMeta?: SelectionInteractionMeta,
    ): boolean {
        const expectedAssemblyNodeId = interactionMeta?.assemblyNodeId?.trim() ?? '';
        if (!expectedAssemblyNodeId) {
            return true;
        }
        if (!ImportedModelObjectMetadataService.isImportedModelObject(object)) {
            return true;
        }

        const selectionTargetAssemblyNodeId = ImportedModelObjectMetadataService
            .resolveSelectionTargetFromSelfOrAncestors(object)
            ?.assemblyNodeId
            ?.trim() ?? '';
        if (selectionTargetAssemblyNodeId === expectedAssemblyNodeId) {
            return true;
        }

        const metadataAssemblyNodeId = ImportedModelObjectMetadataService
            .resolveAssemblyNodeIdFromSelfOrAncestors(object)
            ?.trim() ?? '';
        return metadataAssemblyNodeId === expectedAssemblyNodeId;
    }

    /**
     * 根据对象 ID 解析场景对象。
     *
     * @param objectId 目标对象 ID。
     * @param interactionMeta 程序化选择元信息。
     * @returns 命中的场景对象；未命中时返回 null。
     */
    private resolveSceneObjectById(
        objectId: string,
        interactionMeta?: SelectionInteractionMeta,
    ): THREE.Object3D | null {
        const normalizedObjectId = objectId.trim();
        if (!normalizedObjectId) {
            return null;
        }

        if (typeof this.renderer.getSceneObjectById === 'function') {
            const directHit = this.renderer.getSceneObjectById(normalizedObjectId);
            if (
                directHit
                && this.isImportedSelectionObjectInInteractionScope(directHit, interactionMeta)
            ) {
                return directHit;
            }
        }

        let uuidHit: THREE.Object3D | null = null;
        let importedSelectionAnchorHit: THREE.Object3D | null = null;
        this.renderer.scene.traverse((object) => {
            if (!uuidHit && object.uuid === normalizedObjectId) {
                if (this.isImportedSelectionObjectInInteractionScope(object, interactionMeta)) {
                    uuidHit = object;
                }
                return;
            }

            if (importedSelectionAnchorHit) {
                return;
            }

            const hierarchySelectionObjectId = ImportedModelObjectMetadataService
                .resolveHierarchySelectionObjectId(object)
                ?.trim() ?? '';
            const selectionProxyObjectId = ImportedModelObjectMetadataService
                .resolveSelectionProxyObjectId(object)
                ?.trim() ?? '';
            if (
                hierarchySelectionObjectId === normalizedObjectId
                || selectionProxyObjectId === normalizedObjectId
            ) {
                if (!this.isImportedSelectionObjectInInteractionScope(object, interactionMeta)) {
                    return;
                }
                importedSelectionAnchorHit = object;
            }
        });

        return uuidHit ?? importedSelectionAnchorHit;
    }

    /**
     * 设置拖拽能力配置。
     * @param config 拖拽能力配置增量。
     */
    public setDragCapabilityConfig(config: Partial<EntityDragCapabilityConfig>): void {
        this.defaultSelectionHandler.setDragCapabilityConfig(config);
        this.sketchSelectionHandler.setDragCapabilityConfig(config);
        this.referencePlaneSelectionHandler.setDragCapabilityConfig(config);
        this.referenceAxisSelectionHandler.setDragCapabilityConfig(config);
        this.referencePointSelectionHandler.setDragCapabilityConfig(config);
        //------ 装配体模式下基准点线面 开始------
        this.assemblyReferencePlaneSelectionHandler.setDragCapabilityConfig(config);
        this.assemblyReferenceAxisSelectionHandler.setDragCapabilityConfig(config);
        this.assemblyReferencePointSelectionHandler.setDragCapabilityConfig(config);
        //------ 装配体模式下基准点线面 结束------
        this.sketchTrimSelectionHandler.setDragCapabilityConfig(config);
    }

    /**
     * 发布框选事件。
     * @param payload 框选事件载荷。
     */
    public publishEntityBoxSelected(payload: EntityBoxSelectedEventPayload): void {
        this.publishInteractionEvent(EntityInteractionEventType.EntityBoxSelected, payload);
    }

    /**
     * 发布多实体框选事件。
     * @param selectedObjects 命中对象列表。
     * @param isCrossing 是否 crossing 模式。
     */
    public publishMultipleEntitiesBoxSelected(selectedObjects: THREE.Object3D[], isCrossing: boolean = false): void {
        this.publishInteractionEvent(EntityInteractionEventType.MultipleEntitiesBoxSelected, {
            type: EntityInteractionEventType.MultipleEntitiesBoxSelected,
            selectedObjectIds: selectedObjects.map((item) => item.uuid),
            selectedObjects,
            isCrossing,
            timestamp: Date.now(),
        });
    }

    /**
     * 判断当前是否处于绘图激活态。
     * @returns 是否处于绘图态。
     */
    public isSketchDrawingActive(): boolean {
        return Boolean(this.renderer.sketchDrawingManager?.isDrawingActive());
    }

    /**
     * 获取当前选中的点对象。
     * @returns 当前选中点对象。
     */
    public getCurrentSelectedPoint(): SketchPointObject | null {
        return this.currentSelectedPoint;
    }

    /**
     * 获取当前激活拖拽策略。
     * @returns 当前拖拽策略。
     */
    public getActiveDragStrategy(): AbstractEntityDragStrategy | null {
        return this.activeDragStrategy;
    }

    /**
     * 设置当前激活拖拽策略。
     * @param strategy 拖拽策略实例。
     */
    public setActiveDragStrategy(strategy: AbstractEntityDragStrategy | null): void {
        this.activeDragStrategy = strategy;
    }

    /**
     * 获取鼠标按下起点。
     * @returns 鼠标按下起点。
     */
    public getMouseDownStart(): THREE.Vector2 | null {
        return this.mouseDownStart;
    }

    /**
     * 设置鼠标按下起点。
     * @param mouseDownStart 鼠标按下起点。
     */
    public setMouseDownStart(mouseDownStart: THREE.Vector2 | null): void {
        this.mouseDownStart = mouseDownStart;
    }

    /**
     * 获取是否处于框选拖拽。
     * @returns 是否处于框选拖拽。
     */
    public getIsBoxDragging(): boolean {
        return this.isBoxDragging;
    }

    /**
     * 设置是否处于框选拖拽。
     * @param isBoxDragging 是否处于框选拖拽。
     */
    public setIsBoxDragging(isBoxDragging: boolean): void {
        this.isBoxDragging = isBoxDragging;
    }

    /**
     * 设置框选拖拽终点。
     * @param boxDragEnd 框选拖拽终点。
     */
    public setBoxDragEnd(boxDragEnd: THREE.Vector2 | null): void {
        this.boxDragEnd = boxDragEnd;
    }

    /**
     * 获取框选阈值。
     * @returns 框选阈值。
     */
    public getBoxDragThreshold(): number {
        return EntitySelectionManager.BOX_DRAG_THRESHOLD;
    }

    /**
     * 获取框选框 DOM 元素。
     * @returns 框选框元素。
     */
    public getSelectionBoxElement(): HTMLDivElement | null {
        return this.selectionBoxElement;
    }

    /**
     * 设置框选框 DOM 元素。
     * @param selectionBoxElement 框选框元素。
     */
    public setSelectionBoxElement(selectionBoxElement: HTMLDivElement | null): void {
        this.selectionBoxElement = selectionBoxElement;
    }

    /**
     * 获取是否忽略下一次 click。
     * @returns 是否忽略下一次 click。
     */
    public getSuppressNextClick(): boolean {
        return this.suppressNextClick;
    }

    /**
     * 设置是否忽略下一次 click。
     * @param suppressNextClick 是否忽略下一次 click。
     */
    public setSuppressNextClick(suppressNextClick: boolean): void {
        this.suppressNextClick = suppressNextClick;
    }

    /**
     * 释放资源。
     * 以编程方式设置多个选中对象。
     *
     * @param entities 目标对象集合。
     */
    public selectEntitiesProgrammatically(entities: THREE.Object3D[]): void {
        SelectionStateHelpers.updateSelectionState(
            this as unknown as EntitySelectionManagerRuntime,
            SelectionQueryHelpers.toIntersections(entities),
        );
    }

    /**
     * 收集点击与 Hover 共用的实体候选。
     *
     * @returns 生产 Viewer 返回 Manager 自有的可复用缓冲，调用方必须在当前同步查询内消费；
     * 未注入成员源的兼容宿主继续执行旧完整遍历。
     */
    public collectPointingSelectionTargets(): THREE.Object3D[] {
        if (!this.selectionCandidateIndex) {
            return SelectionQueryHelpers.collectHoverSelectionTargets(this.renderer.scene);
        }
        this.selectionCandidateIndex.writePointingTargets(this.pointingSelectionTargetBuffer);
        return this.pointingSelectionTargetBuffer;
    }

    /**
     * 收集框选候选。
     *
     * @returns 当前可见且符合框选语义的 Manager 自有可复用缓冲，调用方不得跨查询持有。
     */
    public collectBoxSelectionTargets(): THREE.Object3D[] {
        if (!this.selectionCandidateIndex) {
            return SelectionQueryHelpers.collectBoxSelectionTargets(this.renderer.scene);
        }
        this.selectionCandidateIndex.writeBoxTargets(this.boxSelectionTargetBuffer);
        return this.boxSelectionTargetBuffer;
    }

    /**
     * 读取稳定候选索引统计。
     *
     * @returns 生产成员源已接入时返回统计；兼容完整遍历路径返回 null。
     */
    public getSelectionCandidateIndexStats(): EntitySelectionCandidateIndexStats | null {
        return this.selectionCandidateIndex?.getStats() ?? null;
    }

    /**
     * 读取普通 Hover 输入合并统计。
     *
     * @returns 调度器当前累计工作量快照；不会消费或创建 Hover 任务。
     */
    public getHoverInputStats(): EntityHoverInputSchedulerStats {
        return this.hoverInputScheduler.getStats();
    }

    /**
     * 读取点、线、面稳定类型分区统计。
     *
     * @returns 当前选择会话的查询、候选访问及分类缓存命中计数。
     */
    public getSelectionQueryPartitionStats(): EntitySelectionQueryPartitionStats {
        return this.entitySelectionQueryPartitioner.getStats();
    }

    /**
     * 释放事件桥接、选择状态和事件流资源。
     */
    dispose(): void {
        // 先停止桥接，确保不再接收底层事件。
        this.stop();
        // stop 允许随后恢复，而且未启动时会提前返回；真正销毁必须无条件归还全部模式的资源。
        this.ownedSelectionHandlers.forEach((handler) => handler.dispose());
        this.ownedSelectionHandlers.clear();
        this.hoverInputScheduler.dispose();
        this.selectionCandidateIndex?.dispose();
        this.entitySelectionQueryPartitioner.dispose();
        this.pointingSelectionTargetBuffer.length = 0;
        this.boxSelectionTargetBuffer.length = 0;
        (Object.values(EntityInteractionEventType) as PuppyCAD.EntityInteractionEventType[]).forEach((eventType) => {
            this.eventStreams[eventType].complete();
        });
    }

    /**
     * 是否将指针事件路由到测量叠加层。
     *
     * @returns 是否路由到测量处理器。
     */
    private shouldRoutePointerToMeasureHandler(): boolean {
        return this.renderer.measureController.isPickEnabled();
    }

    /**
     * 登记纯 Hover 查询，不延后原始输入、拖拽采样或命令预览。
     * @param consumer 负责解释候选与命中优先级的当前处理器。
     * @param clientX 最新客户端 X 坐标。
     * @param clientY 最新客户端 Y 坐标。
     */
    public queueHoverInput(consumer: EntityHoverInputConsumer, clientX: number, clientY: number): void {
        this.hoverInputScheduler.enqueue(consumer, clientX, clientY);
    }

    /** 模型、实例位姿或选择可见性变化后，使静止指针下的查询失效。 */
    public invalidateHoverPick(): void {
        this.hoverInputScheduler.invalidate();
    }

    /** 由 Viewer 在相机更新后调用；本帧只消费一个最新 Hover 查询。 */
    public flushPendingHoverInput(): void {
        if (!this.validateHoverInputOwner()) return;
        const camera = this.renderer.camera;
        if (camera) {
            camera.updateMatrixWorld();
            this.hoverInputScheduler.observeCamera(camera.matrixWorld.elements, camera.projectionMatrix.elements);
        }
        this.hoverInputScheduler.flush();
    }

    /** @returns 输入仍属于当前选择/测量处理器；测量路由改变也不得重放旧查询。 */
    private validateHoverInputOwner(): boolean {
        const owner = this.hoverInputScheduler.owner;
        if (!owner) return false;
        const activeConsumer = this.shouldRoutePointerToMeasureHandler()
            ? this.measureSelectionHandler : this.activeSelectionHandler;
        if (owner !== activeConsumer) {
            // 路由变化不仅使坐标失效，旧路由已经显示的 Hover 也必须清理。
            this.handlePointerContextInvalidated();
            return false;
        }
        return true;
    }

    /** 无按键离开、失焦与取消不触发业务提交，只清理悬停及待查询坐标。 */
    private readonly handlePointerContextInvalidated = (): void => {
        this.hoverInputScheduler.cancel();
        this.activeSelectionHandler.clearHoverState();
    };

    /** @param payload 当前按下或点击事件，结算其真实坐标而不是旧输入坐标。 */
    private prepareHoverInputBoundary(payload: RawPointerEventPayload): void {
        if (this.validateHoverInputOwner()) {
            this.hoverInputScheduler.prepareBoundary(payload.clientX, payload.clientY);
        }
    }

    /** 鼠标按下事件处理器。 */
    private readonly handleMouseDown = (payload: RawPointerEventPayload): void => {
        if (payload.nativeEvent.defaultPrevented) {
            this.handlePointerContextInvalidated();
            return;
        }
        this.prepareHoverInputBoundary(payload);
        this.hoverInputScheduler.suspend();
        if (this.shouldRoutePointerToMeasureHandler()) {
            this.measureSelectionHandler.onMouseDown(payload);
            return;
        }
        this.activeSelectionHandler.onMouseDown(payload);
    };

    /** 鼠标移动事件处理器。 */
    private readonly handleMouseMove = (payload: RawPointerEventPayload): void => {
        if (payload.nativeEvent.defaultPrevented) {
            this.handlePointerContextInvalidated();
            return;
        }
        if (payload.buttons !== 0) this.hoverInputScheduler.suspend();
        if (this.shouldRoutePointerToMeasureHandler()) {
            this.measureSelectionHandler.onMouseMove(payload);
            return;
        }
        this.activeSelectionHandler.onMouseMove(payload);
    };

    /** 鼠标抬起事件处理器。 */
    private readonly handleMouseUp = (payload: RawPointerEventPayload): void => {
        if (this.shouldRoutePointerToMeasureHandler()) {
            this.measureSelectionHandler.onMouseUp(payload);
        } else {
            this.activeSelectionHandler.onMouseUp(payload);
        }
        this.hoverInputScheduler.resume(payload.clientX, payload.clientY);
    };

    /** 鼠标会话被视口边界或窗口状态取消时，统一释放当前交互状态。 */
    private readonly handlePointerCancel = (payload: RawPointerEventPayload): void => {
        this.handlePointerContextInvalidated();
        if (this.shouldRoutePointerToMeasureHandler()) {
            this.measureSelectionHandler.onPointerCancel(payload);
            return;
        }
        this.activeSelectionHandler.onPointerCancel(payload);
    };

    /** 单击事件处理器。 */
    private readonly handleClick = (payload: RawPointerEventPayload): void => {
        if (payload.nativeEvent.defaultPrevented) {
            this.handlePointerContextInvalidated();
            return;
        }
        this.prepareHoverInputBoundary(payload);
        if (this.shouldRoutePointerToMeasureHandler()) {
            this.measureSelectionHandler.onClick(payload);
            return;
        }
        this.activeSelectionHandler.onClick(payload);
    };

    /** 双击仍由原始事件订阅方即时处理，只先结算本选择领域的最新 Hover。 */
    private readonly handleDoubleClick = (payload: RawPointerEventPayload): void => {
        if (payload.nativeEvent.defaultPrevented) this.handlePointerContextInvalidated();
        else this.prepareHoverInputBoundary(payload);
    };

    //------ 模式感知 开始------
    /**
     * 设置当前交互模式并直接切换激活处理器。
     * 说明：
     * 1. 由 Viewer.setMode 显式推送，不再依赖懒读取 renderer.getMode；
     * 2. 模式切换时清除 hover 状态，防止残留高亮跨模式持续；
     * 3. 直接 switch 赋値 activeSelectionHandler，当后事件就不再运行时解析。
     * @param mode 目标模式。
     */
    public setMode(mode: ViewerMode): void {
        if (this.currentMode === mode) {
            return;
        }
        this.hoverInputScheduler.cancel();
        this.currentMode = mode;

        // 模式切换时主动清除 hover 状态，避免旧模式悬浮对象在新模式下残留高亮。
        if (this.hoveredPick) {
            this.publishEntityHoverLeave(this.hoveredPick.object);
            this.hoveredPick = null;
        }

        //------ 模式切换框选状态收口 开始------
        // 模式切换时清空拖拽与框选运行态，避免旧模式交互状态泄漏到新模式。
        this.stopDragInternal();
        this.mouseDownStart = null;
        this.isBoxDragging = false;
        this.boxDragEnd = null;
        this.resetSelectionBoxVisualInternal();
        this.suppressNextClick = false;
        this.currentHoverInteractionMeta = undefined;
        this.syncImportedTopologyProxyRetention();
        //------ 模式切换框选状态收口 结束------

        // 直接按模式赋値，后续事件指向确定的处理器实例。
        switch (mode) {
            //------ 装配体模式 开始------
            case ViewerMode.Assembly:
                this.activeSelectionHandler = this.assemblySelectionHandler;
                break;
            case ViewerMode.MoveComponent:
                this.activeSelectionHandler = this.moveComponentSelectionHandler;
                break;
            //------ 线性零部件阵列 开始------
            case ViewerMode.LinearComponentPattern:
                this.activeSelectionHandler = this.linearComponentPatternSelectionHandler;
                break;
            //------ 线性零部件阵列 结束------
            //------ 装配体模式 结束------
            case ViewerMode.Sketch:
            case ViewerMode.SketchDrawing:
                this.activeSelectionHandler = this.sketchSelectionHandler;
                break;
            case ViewerMode.SketchMoveEntities:
                this.activeSelectionHandler = this.sketchMoveEntitiesSelectionHandler;
                break;
            case ViewerMode.SketchCopyEntities:
                this.activeSelectionHandler = this.sketchCopyEntitiesSelectionHandler;
                break;
            case ViewerMode.SketchRotateEntities:
                this.activeSelectionHandler = this.sketchRotateEntitiesSelectionHandler;
                break;
            case ViewerMode.SketchCircularPattern:
                this.activeSelectionHandler = this.sketchCircularPatternSelectionHandler;
                break;
            case ViewerMode.SketchMirrorEntities:
                this.activeSelectionHandler = this.sketchMirrorEntitiesSelectionHandler;
                break;
            case ViewerMode.ReferencePlane:
                this.activeSelectionHandler = this.referencePlaneSelectionHandler;
                break;
            case ViewerMode.ReferenceAxis:
                this.activeSelectionHandler = this.referenceAxisSelectionHandler;
                break;
            case ViewerMode.ReferencePoint:
                this.activeSelectionHandler = this.referencePointSelectionHandler;
                break;
            //------ 装配体模式下基准点线面 开始------
            case ViewerMode.AssemblyReferencePlane:
                this.activeSelectionHandler = this.assemblyReferencePlaneSelectionHandler;
                break;
            case ViewerMode.AssemblyReferenceAxis:
                this.activeSelectionHandler = this.assemblyReferenceAxisSelectionHandler;
                break;
            case ViewerMode.AssemblyReferencePoint:
                this.activeSelectionHandler = this.assemblyReferencePointSelectionHandler;
                break;
            //------ 装配体模式下基准点线面 结束------
            case ViewerMode.SketchTrim:
                this.activeSelectionHandler = this.sketchTrimSelectionHandler;
                break;
            //------ 智能尺寸 开始------
            case ViewerMode.SketchDimension:
                this.activeSelectionHandler = this.sketchDimensionSelectionHandler;
                break;
            //------ 智能尺寸 结束------
            case ViewerMode.SketchOffset:
                this.activeSelectionHandler = this.sketchOffsetSelectionHandler;
                break;
            default:
                this.activeSelectionHandler = this.defaultSelectionHandler;
        }
    }
    //------ 模式感知 结束------

    /**
     * 内部停止当前拖拽策略并提交草图变更。
     */
    private stopDragInternal(): void {
        if (!this.activeDragStrategy) {
            return;
        }

        const activeDragStrategy = this.activeDragStrategy;
        const affectedSketchId = activeDragStrategy.resolveAffectedSketchId();
        const hasGeometryChanged = activeDragStrategy.hasGeometryChanged();
        activeDragStrategy.stop();
        if (hasGeometryChanged && affectedSketchId) {
            this.renderer.sketchGroupManager?.getGroupById(affectedSketchId)?.commitDefinitionChanges({
                reason: 'drag-finished',
            });
        }
        this.activeDragStrategy = null;
    }

    /**
     * 清空当前选中的快捷引用。
     */
    private clearCurrentSelectedEntitiesInternal(): void {
        this.currentSelectedPoint = null;
        this.currentSelectedLine = null;
        this.currentSelectedCircle = null;
        this.currentSelectedArc = null;
        this.currentSelectedEllipse = null;
        this.currentSelectedSpline = null;
        this.currentSelectedStyleSpline = null;
        this.currentSelectedSlot = null;
    }

    /**
     * 重置框选可视化。
     */
    private resetSelectionBoxVisualInternal(): void {
        if (this.selectionBoxElement && this.selectionBoxElement.parentElement) {
            this.selectionBoxElement.parentElement.removeChild(this.selectionBoxElement);
        }
        this.selectionBoxElement = null;
    }

    /**
     * 查找对象父链上的轮廓对象。
     * @param object 起始对象。
     * @returns 轮廓对象，未命中返回 null。
     */
    private findContourAncestor(object: THREE.Object3D): SketchContour | null {
        let current: THREE.Object3D | null = object;
        while (current) {
            if (isSketchContour(current)) {
                return current;
            }
            current = current.parent;
        }
        return null;
    }

    /**
     * 根据当前选中状态同步快捷引用。
     * @param selectedPicks 当前选中命中。
     */
    private syncCurrentSelectedEntitiesInternal(selectedPicks: THREE.Intersection<THREE.Object3D>[]): void {
        const primaryObject = selectedPicks[0]?.object ?? null;
        if (!primaryObject) {
            this.clearCurrentSelectedEntitiesInternal();
            return;
        }

        if (primaryObject instanceof SketchPointObject) {
            this.clearCurrentSelectedEntitiesInternal();
            this.currentSelectedPoint = primaryObject;
            return;
        }
        if (primaryObject instanceof SketchLineObject) {
            this.clearCurrentSelectedEntitiesInternal();
            this.currentSelectedLine = primaryObject;
            return;
        }
        if (isSketchCircleObject(primaryObject)) {
            this.clearCurrentSelectedEntitiesInternal();
            this.currentSelectedCircle = primaryObject;
            return;
        }
        if (isSketchArcObject(primaryObject)) {
            this.clearCurrentSelectedEntitiesInternal();
            this.currentSelectedArc = primaryObject;
            return;
        }
        if (isSketchEllipseObject(primaryObject)) {
            this.clearCurrentSelectedEntitiesInternal();
            this.currentSelectedEllipse = primaryObject;
            return;
        }
        if (isSketchStyleSplineObject(primaryObject)) {
            this.clearCurrentSelectedEntitiesInternal();
            // 样式样条拥有独立属性面板，必须优先写入样式样条快捷引用。
            this.currentSelectedStyleSpline = primaryObject;
            return;
        }
        if (isSketchSplineObject(primaryObject)) {
            this.clearCurrentSelectedEntitiesInternal();
            this.currentSelectedSpline = primaryObject;
            return;
        }
        if (isSketchSlotObject(primaryObject)) {
            this.clearCurrentSelectedEntitiesInternal();
            this.currentSelectedSlot = primaryObject;
            return;
        }

        this.clearCurrentSelectedEntitiesInternal();
    }

    /**
     * 派发当前选中快捷事件。
     */
    private publishCurrentSelectionEventInternal(): void {
        this.publishInteractionEvent(EntityInteractionEventType.SelectionCleared, {
            type: EntityInteractionEventType.SelectionCleared,
            timestamp: Date.now(),
        });

        if (this.currentSelectedPoint) {
            this.publishInteractionEvent(EntityInteractionEventType.PointSelected, {
                type: EntityInteractionEventType.PointSelected,
                objectId: this.currentSelectedPoint.uuid,
                object: this.currentSelectedPoint,
                timestamp: Date.now(),
            });
            return;
        }
        if (this.currentSelectedLine) {
            this.publishInteractionEvent(EntityInteractionEventType.LineSelected, {
                type: EntityInteractionEventType.LineSelected,
                objectId: this.currentSelectedLine.uuid,
                object: this.currentSelectedLine,
                timestamp: Date.now(),
            });
            return;
        }
        if (this.currentSelectedCircle) {
            this.publishInteractionEvent(EntityInteractionEventType.CircleSelected, {
                type: EntityInteractionEventType.CircleSelected,
                objectId: this.currentSelectedCircle.uuid,
                object: this.currentSelectedCircle,
                timestamp: Date.now(),
            });
            return;
        }
        if (this.currentSelectedArc) {
            this.publishInteractionEvent(EntityInteractionEventType.ArcSelected, {
                type: EntityInteractionEventType.ArcSelected,
                objectId: this.currentSelectedArc.uuid,
                object: this.currentSelectedArc,
                timestamp: Date.now(),
            });
            return;
        }
        if (this.currentSelectedEllipse) {
            this.publishInteractionEvent(EntityInteractionEventType.EllipseSelected, {
                type: EntityInteractionEventType.EllipseSelected,
                objectId: this.currentSelectedEllipse.uuid,
                object: this.currentSelectedEllipse,
                timestamp: Date.now(),
            });
            return;
        }
        if (this.currentSelectedStyleSpline) {
            this.publishInteractionEvent(EntityInteractionEventType.StyleSplineSelected, {
                type: EntityInteractionEventType.StyleSplineSelected,
                objectId: this.currentSelectedStyleSpline.uuid,
                object: this.currentSelectedStyleSpline,
                timestamp: Date.now(),
            });
            return;
        }
        if (this.currentSelectedSpline) {
            this.publishInteractionEvent(EntityInteractionEventType.SplineSelected, {
                type: EntityInteractionEventType.SplineSelected,
                objectId: this.currentSelectedSpline.uuid,
                object: this.currentSelectedSpline,
                timestamp: Date.now(),
            });
            return;
        }
        if (this.currentSelectedSlot) {
            this.publishInteractionEvent(EntityInteractionEventType.SlotSelected, {
                type: EntityInteractionEventType.SlotSelected,
                objectId: this.currentSelectedSlot.uuid,
                object: this.currentSelectedSlot,
                timestamp: Date.now(),
            });
            return;
        }

        //------ 特征实体选择事件 开始------
        const primaryObject = this.selectedPicks[0]?.object ?? null;
        const selectedObjects = this.selectedPicks.map((pick) => pick.object);
        const timestamp = Date.now();

        if (primaryObject && isFeatureFace(primaryObject)) {
            const selectedFeatureFaces = selectedObjects.filter((object): object is FeatureFace => isFeatureFace(object));
            this.publishInteractionEvent(EntityInteractionEventType.FeatureFaceSelected, {
                type: EntityInteractionEventType.FeatureFaceSelected,
                objectId: primaryObject.uuid,
                object: primaryObject,
                selectedObjectIds: selectedFeatureFaces.map((object) => object.uuid),
                selectedObjects: selectedFeatureFaces,
                timestamp,
            });
            return;
        }

        if (primaryObject && isFeatureLine(primaryObject)) {
            const selectedFeatureLines = selectedObjects.filter((object): object is FeatureLine => isFeatureLine(object));
            this.publishInteractionEvent(EntityInteractionEventType.FeatureLineSelected, {
                type: EntityInteractionEventType.FeatureLineSelected,
                objectId: primaryObject.uuid,
                object: primaryObject,
                selectedObjectIds: selectedFeatureLines.map((object) => object.uuid),
                selectedObjects: selectedFeatureLines,
                timestamp,
            });
            return;
        }

        if (primaryObject && isFeaturePoint(primaryObject)) {
            const selectedFeaturePoints = selectedObjects.filter((object): object is FeaturePoint => isFeaturePoint(object));
            this.publishInteractionEvent(EntityInteractionEventType.FeaturePointSelected, {
                type: EntityInteractionEventType.FeaturePointSelected,
                objectId: primaryObject.uuid,
                object: primaryObject,
                selectedObjectIds: selectedFeaturePoints.map((object) => object.uuid),
                selectedObjects: selectedFeaturePoints,
                timestamp,
            });
        }
        //------ 特征实体选择事件 结束------
    }

    /**
     * 当 hover 与当前选中冲突时清空 hover。
     */
    private clearHoverWhenSelectionConflictsInternal(): void {
        if (!this.hoveredPick) {
            return;
        }
        if (this.isHoverMutuallyExclusiveWithSelectionInternal(this.hoveredPick)) {
            const previousHoveredPick = this.hoveredPick;
            this.hoveredPick = null;
            this.publishInteractionEvent(EntityInteractionEventType.EntityHoverLeave, {
                type: EntityInteractionEventType.EntityHoverLeave,
                objectId: previousHoveredPick.object.uuid,
                object: previousHoveredPick.object,
                timestamp: Date.now(),
            });
        }
    }

    /**
     * 判断 hover 与选中是否互斥。
     * @param pick 命中对象。
     * @returns 是否互斥。
     */
    private isHoverMutuallyExclusiveWithSelectionInternal(pick: THREE.Intersection<THREE.Object3D>): boolean {
        if (!(pick.object instanceof SketchLineObject) && !isFeatureLine(pick.object)) {
            return false;
        }
        return this.selectedPicks.some((selectedPick) => this.isSamePickInternal(selectedPick, pick));
    }

    /**
     * 判断两个命中是否相同。
     * @param left 左命中。
     * @param right 右命中。
     * @returns 是否相同。
     */
    private isSamePickInternal(
        left: THREE.Intersection<THREE.Object3D> | null,
        right: THREE.Intersection<THREE.Object3D> | null,
    ): boolean {
        if (left === right) {
            return true;
        }
        if (!left || !right) {
            return false;
        }
        return left.object.uuid === right.object.uuid;
    }

    /**
     * 按对象 ID 去重选择命中集合。
     * @param picks 原始命中集合。
     * @returns 按首次出现顺序保留的唯一命中集合。
     */
    private normalizeSelectionPicksByObject(
        picks: THREE.Intersection<THREE.Object3D>[],
    ): THREE.Intersection<THREE.Object3D>[] {
        const uniqueMap = new Map<string, THREE.Intersection<THREE.Object3D>>();
        picks.forEach((item) => {
            const objectId = item.object.uuid;
            if (!uniqueMap.has(objectId)) {
                uniqueMap.set(objectId, item);
            }
        });
        return Array.from(uniqueMap.values());
    }

    /**
     * 判断两个命中数组是否一致。
     * @param left 左数组。
     * @param right 右数组。
     * @returns 是否一致。
     */
    private isPickArrayEqual(
        left: THREE.Intersection<THREE.Object3D>[],
        right: THREE.Intersection<THREE.Object3D>[],
    ): boolean {
        if (left.length !== right.length) {
            return false;
        }
        for (let index = 0; index < left.length; index += 1) {
            if (!this.isSamePickInternal(left[index], right[index])) {
                return false;
            }
        }
        return true;
    }

    /**
     * 发布实体交互事件。
     * @param type 事件类型。
     * @param payload 事件载荷。
     */
    private publishInteractionEvent(
        type: PuppyCAD.EntityInteractionEventType,
        payload: EntityInteractionEventPayloadMap[PuppyCAD.EntityInteractionEventType],
    ): void {
        (this.eventStreams[type] as Subject<EntityInteractionEventPayloadMap[PuppyCAD.EntityInteractionEventType]>).next(payload);
    }
}
